/**
 * Centralna baza wpisów blogowych. Każdy artykuł = osobny obiekt + osobna route w src/routes/blog.{slug}.tsx
 *
 * Treść = MDX-like markdown w polu `content` (renderowane przez prosty parser w ArticleLayout).
 * Pełne wsparcie: nagłówki h2/h3, listy, pogrubienia, linki, callout boxy.
 */

export interface BlogPost {
  slug: string;
  title: string; // pełny H1 + meta title
  metaDescription: string; // 150-160 znaków pod SEO
  excerpt: string; // krótki opis na liście
  category: "Baselinker" | "Integracje" | "Marketing" | "Migracje" | "Płatności" | "Logistyka";
  publishedAt: string; // ISO date
  readingTime: number; // minuty
  keywords: string[]; // long-tail keywords
  toc: { id: string; label: string }[];
  content: string; // markdown-light (wewnętrzny format)
  related: string[]; // slugi powiązanych wpisów
  faqs?: { q: string; a: string }[]; // FAQ na końcu artykułu + dane strukturalne FAQPage
  ogTitle?: string; // skrócony tytuł pod social share (max ~60 znaków)
  ogDescription?: string; // opis pod social share (max ~110 znaków, klikalny)
  ogImage?: string; // absolutny URL obrazka 1200x630 do social share
}

const PL_DATE_2026_03 = "2026-03-15";
const PL_DATE_2026_03_22 = "2026-03-22";
const PL_DATE_2026_03_28 = "2026-03-28";
const PL_DATE_2026_04_02 = "2026-04-02";
const PL_DATE_2026_04_08 = "2026-04-08";
const PL_DATE_2026_04_12 = "2026-04-12";
const PL_DATE_2026_04_16 = "2026-04-16";
const PL_DATE_2026_04_20 = "2026-04-20";
const PL_DATE_2026_04_25 = "2026-04-25";
const PL_DATE_2026_04_30 = "2026-04-30";
const PL_DATE_2026_05_05 = "2026-05-05";

export const BLOG_POSTS: BlogPost[] = [
  // ============================================
  // 1. BaseLinker × Shopify
  // ============================================
  {
    slug: "jak-polaczyc-baselinker-z-shopify",
    title: "Jak połączyć Baselinker z Shopify krok po kroku — przewodnik 2026",
    metaDescription:
      "Krok po kroku jak zintegrować Baselinker z Shopify: konfiguracja, mapowanie produktów, automatyzacje, najczęstsze błędy. Praktyczny przewodnik dla sklepów e-commerce.",
    excerpt:
      "Kompletny przewodnik integracji Baselinker z Shopify — od pierwszej konfiguracji po zaawansowane automatyzacje. Pokazuję dokładnie, co skonfigurować i jakich błędów unikać.",
    category: "Integracje",
    publishedAt: PL_DATE_2026_03,
    readingTime: 12,
    keywords: ["baselinker shopify integracja", "shopify baselinker", "integracja shopify baselinker"],
    ogTitle: "Baselinker × Shopify — integracja krok po kroku 2026",
    ogDescription: "Konfiguracja, mapowanie produktów i automatyzacje. Praktyczny przewodnik integracji Baselinker z Shopify.",
    toc: [
      { id: "dlaczego-warto", label: "Dlaczego warto łączyć Baselinker z Shopify" },
      { id: "wymagania", label: "Wymagania wstępne" },
      { id: "konfiguracja", label: "Konfiguracja krok po kroku" },
      { id: "mapowanie", label: "Mapowanie produktów i atrybutów" },
      { id: "automatyzacje", label: "Najważniejsze automatyzacje" },
      { id: "bledy", label: "Najczęstsze błędy i jak ich unikać" },
      { id: "podsumowanie", label: "Podsumowanie" },
    ],
    related: ["baselinker-koszty-wdrozenia-2026", "automatyzacja-zamowien-baselinker", "migracja-z-shopgold-do-baselinker"],
    content: `## {dlaczego-warto} Dlaczego warto łączyć Baselinker z Shopify

Shopify świetnie sprzedaje, ale operacyjnie zostawia sporo do życzenia. Brakuje zintegrowanego centrum zarządzania zamówieniami z wielu kanałów (Allegro, Amazon, eBay), automatyzacji statusów, masowego wystawiania ofert czy bezpośrednich integracji z polskimi kurierami i systemami ERP.

**Baselinker uzupełnia te luki idealnie.** Po połączeniu obu systemów Shopify zostaje frontendem sprzedaży, a Baselinker staje się centrum operacyjnym — w jednym ekranie widzisz zamówienia ze wszystkich kanałów, generujesz listy przewozowe, automatyzujesz statusy i synchronizujesz magazyn.

### Co dokładnie zyskujesz po integracji

- **Centralne zarządzanie zamówieniami** ze sklepu Shopify + wszystkich marketplace'ów
- **Automatyczne nadawanie listów przewozowych** dla InPost, DPD, DHL, GLS
- **Synchronizację stanów magazynowych** w czasie rzeczywistym między Shopify a marketplace'ami
- **Automatyczne fakturowanie** i wysyłkę dokumentów do klienta
- **Mapowanie produktów Shopify** na oferty Allegro/Amazon z poziomu jednego panelu

> **W praktyce:** zespół, który obsługiwał 200 zamówień dziennie ręcznie, po integracji obsługuje 600 zamówień bez zwiększania liczby etatów.

## {wymagania} Wymagania wstępne

Zanim zaczniesz, upewnij się, że masz:

- **Aktywne konto Shopify** (dowolny plan — Basic, Shopify, Advanced, Plus)
- **Konto Baselinker** (najlepiej plan PRO lub wyższy ze względu na limity API)
- **Dane administracyjne** do obu systemów
- **Listę kategorii i atrybutów** sklepu Shopify (do mapowania)
- **Spis aktywnych marketplace'ów** (Allegro, Amazon, Empik) — jeśli chcesz je też zintegrować

Czas potrzebny na podstawową integrację: **2-4 godziny** dla sklepu z ~500 produktami. Większe katalogi i bardziej zaawansowane konfiguracje to 1-3 dni roboczych.

## {konfiguracja} Konfiguracja krok po kroku

### Krok 1: Wygenerowanie klucza API w Shopify

W panelu Shopify przejdź do **Settings → Apps and sales channels → Develop apps**. Kliknij **Create an app**, nadaj nazwę (np. "Baselinker Integration"), a następnie w zakładce **Configuration → Admin API access scopes** włącz uprawnienia:

- **Products** — read & write
- **Orders** — read & write
- **Inventory** — read & write
- **Customers** — read
- **Shipping** — read

Po zapisaniu wygeneruj token dostępowy (**Admin API access token**) — będzie potrzebny w Baselinkerze.

### Krok 2: Dodanie Shopify w panelu Baselinker

W Baselinkerze otwórz **Integracje → Sklepy → Dodaj nowy → Shopify**. Wpisz:

- adres sklepu (np. \`mojsklep.myshopify.com\`)
- token Admin API z poprzedniego kroku
- walutę domyślną
- kategorię docelową dla zamówień

Po zapisaniu Baselinker przeprowadzi pierwszy test połączenia. Jeśli wszystko jest OK, zobaczysz zielony status **Połączono**.

### Krok 3: Pierwsza synchronizacja produktów

Uruchom **Synchronizację katalogu** — Baselinker pobierze wszystkie produkty z Shopify wraz z wariantami, zdjęciami, cenami i stanami. Dla 1000 produktów zajmuje to ok. 5-10 minut.

> **Uwaga:** podczas pierwszej synchronizacji nie wprowadzaj zmian w produktach po stronie Shopify — może to spowodować konflikty.

### Krok 4: Konfiguracja synchronizacji zwrotnej

W ustawieniach integracji włącz:

- **Pobieranie zamówień** (co 5 minut zalecane)
- **Synchronizację stanów magazynowych** (Baselinker → Shopify)
- **Aktualizację statusów zamówień** (Baselinker → Shopify, np. "Wysłane")
- **Wysyłkę numerów trackingu do Shopify** (klient widzi tracking w panelu Shopify)

## {mapowanie} Mapowanie produktów i atrybutów

To etap, na którym najczęściej rozsypują się integracje robione na szybko. Każdy produkt w Shopify ma swoje atrybuty (color, size, material), które trzeba zmapować na strukturę Baselinkera, żeby później poprawnie generować oferty na marketplace'ach.

### Trzy podejścia do mapowania

1. **Automatyczne** — Baselinker próbuje dopasować pola po nazwach. Działa dobrze dla 60-70% produktów, resztę trzeba poprawić ręcznie.
2. **Półautomatyczne** — definiujesz reguły (np. "atrybut Color w Shopify = atrybut Kolor w Baselinkerze"), system stosuje je do wszystkich produktów.
3. **Manualne** — produkt po produkcie, najprecyzyjniejsze, ale czasochłonne.

W praktyce **najlepiej działa podejście półautomatyczne** — definiujesz 10-15 reguł, które pokrywają 95% przypadków, a resztę poprawiasz ręcznie.

## {automatyzacje} Najważniejsze automatyzacje

Po podpięciu Shopify do Baselinkera odblokowujesz cały świat automatyzacji. Oto pięć, które wdrażam w niemal każdym projekcie:

1. **Automatyczna zmiana statusu po opłaceniu** — zamówienie z Shopify ze statusem "Paid" automatycznie trafia do statusu "W realizacji" w Baselinkerze.
2. **Automatyczne generowanie listu przewozowego** — gdy operator przypina kuriera, system od razu generuje etykietę i drukuje ją na drukarce sieciowej.
3. **Automatyczna wysyłka maila z trackingiem** — klient dostaje powiadomienie o nadaniu paczki w ciągu sekund.
4. **Automatyczna synchronizacja stanów** — gdy ktoś kupi na Allegro, stan w Shopify automatycznie się obniża.
5. **Automatyczne tworzenie faktury** — po wysyłce paczki Baselinker generuje fakturę i wysyła ją do klienta + do księgowości.

> **ROI w liczbach:** sklep z 200 zamówień dziennie po wdrożeniu tych pięciu automatyzacji oszczędza średnio 4-5 godzin pracy operatora dziennie.

## {bledy} Najczęstsze błędy i jak ich unikać

### 1. Niewłaściwe uprawnienia API

Najczęstszy problem. Bez pełnych uprawnień Baselinker nie może aktualizować stanów lub statusów. **Rozwiązanie:** sprawdź zakresy uprawnień w sekcji Develop apps w Shopify.

### 2. Brak mapowania kategorii

Produkty trafiają do "kategorii niesklasyfikowanej" w Baselinkerze, co później blokuje wystawianie ofert na Allegro. **Rozwiązanie:** zmapuj kategorie zanim odpalisz pełną synchronizację.

### 3. Konflikty stanów magazynowych

Gdy synchronizacja działa w obie strony bez jasno określonego "źródła prawdy", stany zaczynają się rozjeżdżać. **Rozwiązanie:** ustal, który system jest źródłem prawdy dla magazynu (najczęściej Baselinker) i wyłącz odwrotny kierunek synchronizacji.

### 4. Pominięcie webhooków

Jeśli polegasz tylko na cyklicznym pollingu (co 5 min), zamówienia trafiają z opóźnieniem. **Rozwiązanie:** włącz webhooki Shopify dla nowych zamówień — wpadają do Baselinkera w ciągu sekund.

## {podsumowanie} Podsumowanie

Integracja Baselinker z Shopify to jedno z najlepszych zwrotnych inwestycji, jakie możesz zrobić dla swojego sklepu. Po wdrożeniu zyskujesz:

- centralne zarządzanie zamówieniami z wszystkich kanałów,
- automatyzacje oszczędzające 4-5 godzin pracy dziennie,
- skalowalny fundament pod sprzedaż wielokanałową,
- profesjonalną obsługę klienta (faktury, tracking, statusy).

**Czas na pełne wdrożenie:** 2-4 dni dla średniego sklepu, 1-2 tygodnie dla rozbudowanych projektów z wieloma kanałami.

Jeśli chcesz, żebym zrobił to za Ciebie — odezwij się. Bezpłatna konsultacja, konkretna wycena, rozumiem polski rynek i polski ekosystem ERP.`,
  },

  // ============================================
  // 2. Migracja z ShopGold do BaseLinker
  // ============================================
  {
    slug: "migracja-z-shopgold-do-baselinker",
    title: "Migracja z ShopGold do Baselinker — kompletny przewodnik 2026",
    metaDescription:
      "Jak bezpiecznie zmigrować sklep z ShopGold do Baselinker bez utraty zamówień, danych klientów i pozycji SEO. Krok po kroku, lista kontrolna, najczęstsze pułapki.",
    excerpt:
      "Pełen plan migracji z ShopGold do Baselinker — od audytu po pierwsze zamówienie. Co przenieść, czego nie przenosić i jak nie zatrzymać sprzedaży.",
    category: "Migracje",
    publishedAt: PL_DATE_2026_03_22,
    readingTime: 14,
    keywords: ["migracja shopgold baselinker", "shopgold baselinker", "przeniesienie sklepu shopgold"],
    ogTitle: "Migracja ShopGold → Baselinker — przewodnik 2026",
    ogDescription: "Eksport, mapowanie kategorii i bezstratne przeniesienie sklepu. Krok po kroku z ShopGold do Baselinkera.",
    toc: [
      { id: "kiedy-migrowac", label: "Kiedy warto migrować" },
      { id: "audyt", label: "Audyt przed migracją" },
      { id: "plan", label: "Plan migracji krok po kroku" },
      { id: "dane", label: "Co i jak przenieść" },
      { id: "test", label: "Faza testowa" },
      { id: "switch", label: "Przełączenie produkcji" },
      { id: "po-migracji", label: "Co zrobić po migracji" },
    ],
    related: ["jak-polaczyc-baselinker-z-shopify", "baselinker-koszty-wdrozenia-2026", "automatyzacja-zamowien-baselinker"],
    content: `## {kiedy-migrowac} Kiedy warto migrować z ShopGold do Baselinkera

ShopGold to solidna platforma, ale nie nadaje się do roli centrum operacyjnego. Migracja na Baselinkera ma sens, gdy:

- masz **3+ kanały sprzedaży** (sklep + 2+ marketplace'y),
- zatrudniasz **2+ osoby do obsługi zamówień** ręcznie,
- chcesz **zintegrować ERP** (Comarch, Subiekt, WAPRO, Symfonia),
- planujesz **skalować się ponad 500 zamówień miesięcznie**,
- potrzebujesz **zaawansowanych automatyzacji** (warunkowe scenariusze, multi-magazyn).

Jeśli masz mały sklep z jednym kanałem i 50 zamówień miesięcznie — migracja prawdopodobnie się nie opłaci.

## {audyt} Audyt przed migracją

Najpoważniejszy błąd, jaki widzę u klientów, to migracja bez audytu. Skutek: po przełączeniu okazuje się, że brakuje danych, integracje nie działają, a zespół panikuje. **Audyt zajmuje 1-3 dni i oszczędza tygodnie problemów.**

### Co dokładnie sprawdzić

1. **Liczba i struktura produktów** — kategorie, warianty, atrybuty, zdjęcia, opisy.
2. **Aktywne integracje** — kurierzy, bramki płatności, marketplace'y, ERP, email marketing.
3. **Historię zamówień** — ile, z jakich źródeł, w jakim stanie.
4. **Klientów i ich dane** — RODO, zgody marketingowe, historia zakupów.
5. **Aktywne kampanie** — Google Ads, Facebook Ads, kampanie email, kupony.
6. **Custom rozwiązania** — moduły dedykowane, własne skrypty, niestandardowe szablony.

### Wynik audytu

Po audycie powinieneś mieć **dokument migracyjny** zawierający:

- listę elementów do przeniesienia + sposób przeniesienia,
- listę elementów do **NIE-przeniesienia** (np. martwe produkty, stare promocje),
- listę elementów do przebudowy w nowym systemie (automatyzacje, integracje),
- harmonogram z datami i odpowiedzialnymi osobami,
- plan B na wypadek problemów.

## {plan} Plan migracji krok po kroku

Standardowa migracja ze ShopGold do Baselinkera trwa **3-5 tygodni** i przebiega w sześciu fazach:

1. **Tydzień 1: Audyt + setup Baselinkera** (konfiguracja konta, struktur, użytkowników).
2. **Tydzień 2: Migracja produktów + integracje sklepu** (Shopify/Shoper/PrestaShop jeśli zostaje).
3. **Tydzień 3: Integracje marketplace'ów + kurierów + automatyzacje**.
4. **Tydzień 4: Testy + szkolenia zespołu + faza pilotażowa**.
5. **Tydzień 5: Switch produkcyjny + monitoring + poprawki**.

> **Krytyczna zasada:** Baselinker uruchamiamy **równolegle** ze ShopGoldem, a nie zamiast. Przełączenie następuje dopiero, gdy nowy system działa stabilnie przez minimum 5 dni.

## {dane} Co i jak przenieść

### Produkty

Eksportujesz z ShopGolda w CSV/XML, importujesz do Baselinkera (lub do nowego sklepu, jeśli zmieniasz też platformę). **Zachowaj SKU** — to identyfikator, który łączy produkty między systemami.

### Zamówienia

Zamówienia archiwalne (zakończone) możesz zostawić w ShopGoldzie do wglądu — nie trzeba ich przenosić do Baselinkera. **Zamówienia w realizacji** (wysłane, ale niezakończone) musisz dokończyć w starym systemie albo ręcznie przenieść do nowego.

### Klienci

Eksport klientów z ShopGolda → import do Baselinkera (lub bezpośrednio do email marketingu, jeśli klienci nie są potrzebni operacyjnie). **Pamiętaj o RODO** — jeśli klient zgodził się na newsletter w ShopGoldzie, musi mieć tę samą zgodę w nowym systemie.

### Integracje

**Nie przenoś** integracji 1:1 — to okazja, żeby je przemyśleć od nowa. Część integracji ShopGolda była prawdopodobnie wymuszona ograniczeniami platformy. W Baselinkerze możesz mieć lepsze, prostsze rozwiązania.

## {test} Faza testowa

To najważniejsza, a najczęściej pomijana faza. **Zanim przełączysz cały sklep**, przeprowadź pełen test na 5-10 zamówieniach testowych:

1. Złóż zamówienie testowe w sklepie.
2. Sprawdź, czy spłynęło do Baselinkera w czasie <5 minut.
3. Wygeneruj list przewozowy.
4. Zmień status na "Wysłane".
5. Sprawdź, czy klient dostał maila z trackingiem.
6. Wystaw fakturę — sprawdź, czy trafiła do księgowości.
7. Zwróć produkt — sprawdź, czy proces zwrotu działa.

Jeśli **któryś z tych kroków nie działa**, NIE przełączaj produkcji. Napraw, przetestuj ponownie.

## {switch} Przełączenie produkcji

Kiedy testy są zielone, czas na switch. Najlepszy moment: **niedziela wieczór 22:00-24:00** (najmniej zamówień, masz całą noc na poprawki przed poniedziałkiem).

### Checklista switcha

- [ ] Backup ShopGolda (export wszystkich danych).
- [ ] Wyłączenie ShopGolda jako źródła zamówień.
- [ ] Przełączenie webhooków na Baselinkera.
- [ ] Test 3-5 zamówień testowych w produkcji.
- [ ] Monitoring kolejki zamówień przez 2-4h.
- [ ] Powiadomienie zespołu obsługi.

## {po-migracji} Co zrobić po migracji

Pierwszy tydzień po migracji jest krytyczny. **Codziennie sprawdzaj:**

- liczbę nowych zamówień (czy nie spadła nieoczekiwanie),
- czas przetwarzania zamówienia (od złożenia do wysyłki),
- liczbę reklamacji i zwrotów (nieprzewidziane błędy techniczne),
- statusy paczek u kurierów (czy listy generują się poprawnie),
- raporty marżowe (czy ceny i koszty się zgadzają).

Po 4 tygodniach możesz wyłączyć ShopGolda na dobre. Wcześniej **zachowaj możliwość powrotu** — to Twoja polisa ubezpieczeniowa.

---

Migracja, którą widziałem zrobioną dobrze: 0 utraconych zamówień, 0 godzin downtime, zespół przeszedł na nowy system bez chaosu. Migracja zrobiona źle: 2 tygodnie problemów, 50 niezadowolonych klientów, koszt naprawienia większy niż samo wdrożenie.

**Jeśli chcesz, żebym poprowadził Twoją migrację** — odezwij się. Mam za sobą kilkadziesiąt takich projektów i znam wszystkie pułapki.`,
  },

  // ============================================
  // 3. SellAsist × Allegro
  // ============================================
  {
    slug: "integracja-sellasist-allegro-krok-po-kroku",
    title: "Integracja SellAsist z Allegro krok po kroku — przewodnik 2026",
    metaDescription:
      "Jak skonfigurować integrację SellAsist z Allegro: wystawianie ofert, repricer, automatyzacje, integracja SMART! Krok po kroku z przykładami i screenshotami.",
    excerpt:
      "Allegro to fundament polskiego e-commerce. Pokazuję, jak SellAsist łączy się z Allegro i jak skonfigurować to tak, żeby sprzedawać 3-5x efektywniej.",
    category: "Integracje",
    publishedAt: PL_DATE_2026_03_28,
    readingTime: 11,
    keywords: ["sellasist allegro", "integracja sellasist allegro", "sellasist konfiguracja"],
    ogTitle: "SellAsist × Allegro — integracja krok po kroku 2026",
    ogDescription: "Wystawianie ofert, mapowanie parametrów i automatyczne statusy. Pełen przewodnik integracji SellAsist z Allegro.",
    toc: [
      { id: "dlaczego-sellasist", label: "Dlaczego SellAsist do Allegro" },
      { id: "konto", label: "Konfiguracja konta i API" },
      { id: "wystawianie", label: "Masowe wystawianie ofert" },
      { id: "repricer", label: "Repricer i strategia cenowa" },
      { id: "smart", label: "Integracja z SMART!" },
      { id: "automatyzacje", label: "Automatyzacje SellAsist + Allegro" },
      { id: "tips", label: "Tipy z praktyki" },
    ],
    related: ["automatyzacja-zamowien-baselinker", "jak-polaczyc-baselinker-z-shopify", "google-ads-dla-ecommerce-przewodnik-2026"],
    content: `## {dlaczego-sellasist} Dlaczego SellAsist do Allegro

Allegro to wciąż **król polskiego e-commerce** — ponad 60% wartości całego rynku. Sprzedaż na Allegro bez profesjonalnego narzędzia to recepta na chaos: ręczne wystawianie ofert, manualna zmiana cen, brak kontroli nad stanami i promocjami.

SellAsist został stworzony **specjalnie pod polskie marketplace'y** i pod Allegro w szczególności. Ma natywne wsparcie dla:

- **wszystkich kategorii Allegro** (bez konieczności mapowania ręcznego),
- **opcji SMART!** (darmowa dostawa, zwroty, oznaczenia),
- **repricerów dynamicznych** (cena dostosowuje się do konkurencji),
- **masowych edycji ofert** (zmiana 1000 ofert w 30 sekund),
- **integracji z polskimi kurierami** i polskimi systemami ERP.

> **Alternatywą** dla SellAsista jest Baselinker. SellAsist wygrywa, gdy 80%+ Twojej sprzedaży idzie przez Allegro. Baselinker — gdy masz dywersyfikację kanałów (Shopify, Amazon, własny sklep, Allegro).

## {konto} Konfiguracja konta i API

### Krok 1: Konto SellAsist

Załóż konto na sellasist.pl. **Plan minimalny:** Standard (od ok. 200 zł/mies.) — Lite ma za małe limity dla większości sklepów.

### Krok 2: Połączenie z Allegro

W panelu SellAsist: **Integracje → Allegro → Dodaj konto**. System przekieruje Cię do logowania na Allegro i poprosi o autoryzację OAuth. Po akceptacji wracasz do SellAsista z aktywnym połączeniem.

### Krok 3: Konfiguracja danych nadawcy

W ustawieniach Allegro w SellAsiście wpisz **dane firmy** (NIP, adres, kontakt) — to dane, które będą widoczne na fakturach i listach przewozowych.

### Krok 4: Konfiguracja sposobów dostawy

Skonfiguruj **cenniki dostaw** — to kluczowe, bo Allegro premiuje oferty z niskimi/darmowymi dostawami w wynikach wyszukiwania. Standardowe konfiguracje:

- **Allegro One Box** (Paczkomaty) — 10-12 zł lub gratis powyżej 100 zł,
- **InPost Kurier** — 14-16 zł,
- **DPD Pickup** — 12-14 zł,
- **Odbiór osobisty** — gratis (jeśli masz fizyczny punkt).

## {wystawianie} Masowe wystawianie ofert

Tutaj SellAsist błyszczy najbardziej. Zamiast wystawiać oferty pojedynczo, możesz wystawić **setki ofert jednocześnie** dzięki szablonom.

### Szablony aukcji

W SellAsiście stworzysz **szablon dla kategorii** (np. "Buty męskie zimowe"), który zawiera:

- domyślny opis (HTML z grafiką),
- wymagane atrybuty kategorii Allegro,
- zdjęcia tła i nagłówki marki,
- warunki sprzedaży i informacje o dostawie.

Następnie przypisujesz produkty do szablonu i SellAsist generuje gotowe oferty Allegro za jednym kliknięciem.

### Importy CSV

Jeśli masz katalog produktowy w Excel/CSV, możesz **zaimportować 500-5000 produktów** w jednej operacji. SellAsist pokaże, które produkty mają komplet danych do wystawienia, a które wymagają uzupełnienia.

## {repricer} Repricer i strategia cenowa

Allegro to wojna cenowa. Bez repricera tracisz Buy Box (najwyższą pozycję w wynikach) na rzecz konkurencji, która ma o 10 groszy taniej. **Repricer SellAsista** pozwala automatycznie dostosowywać ceny.

### Strategie repricera

1. **Najniższa cena minus X groszy** — agresywna, ale opłacalna na produktach z dobrą marżą.
2. **Średnia z TOP 5 minus 1%** — bezpieczniejsza, daje stabilną pozycję bez wojny cenowej.
3. **Mark-up od kosztu** — minimalna marża, którą zawsze utrzymujesz, niezależnie od konkurencji.
4. **Strategia mieszana** — dla produktów wysokomarżowych agresywna, dla niskomarżowych mark-up.

> **Z mojego doświadczenia:** sklepy, które wdrożyły dobrze skonfigurowany repricer, podnoszą obrót na Allegro o **20-40% w pierwszym miesiącu**.

## {smart} Integracja z SMART!

SMART! to program Allegro, który daje klientom darmową dostawę i zwroty. Oferty SMART! są **promowane wyżej w wynikach** i mają wyższy współczynnik konwersji.

W SellAsiście możesz oznaczyć oferty jako SMART! masowo — w ustawieniach kategorii lub szablonów. Pamiętaj, że bycie SMART! ma swoje koszty (Allegro pobiera prowizję od dostawy), więc przed włączeniem warto policzyć opłacalność dla konkretnych produktów.

## {automatyzacje} Automatyzacje SellAsist + Allegro

Pięć automatyzacji, które wdrażam w niemal każdym sklepie:

1. **Automatyczne wystawianie po dodaniu produktu** — nowy produkt w bazie automatycznie pojawia się jako oferta Allegro w ramach domyślnego szablonu.
2. **Automatyczne zdejmowanie po wyczerpaniu** — gdy stan magazynowy = 0, oferta automatycznie znika z Allegro.
3. **Automatyczne wznowienie po uzupełnieniu** — gdy stan wraca, oferta wraca na Allegro z zachowaną historią opinii.
4. **Automatyczna zmiana ceny w promocji** — w określonych dniach/godzinach (Black Friday, świąteczne wyprzedaże).
5. **Automatyczne odpowiedzi na typowe pytania** — bot odpowiadający na pytania o dostępność, dostawę, zwroty.

## {tips} Tipy z praktyki

- **Zdjęcia produktowe** mają większy wpływ na CTR oferty niż cena. Inwestuj w jakość zdjęć.
- **Tytuły ofert** powinny zawierać kluczowe słowa wyszukiwania, ale nie być spamowe. Allegro karze za tytuły typu "NOWA OFERTA SUPER OKAZJA TANIO".
- **Opinie** są walutą Allegro. Zachęcaj klientów do wystawiania opinii przez automatyczne maile po dostarczeniu paczki.
- **Allegro Ads** (wewnętrzna reklama) ma zwykle ROAS 4-8x — warto wdrożyć od razu po setupie SellAsista.

---

**Setup SellAsista pod Allegro** to jedno z najlepszych operacyjnych usprawnień, jakie możesz wprowadzić w sklepie sprzedającym przez ten kanał. Po wdrożeniu:

- 80% pracy operacyjnej znika,
- pozycje ofert rosną,
- ROAS na Allegro Ads znacznie się poprawia,
- czas reakcji na zmiany rynkowe spada z dni na minuty.

Jeśli chcesz, żebym wdrożył SellAsista z pełną integracją Allegro w Twoim sklepie — odezwij się. **Standardowe wdrożenie 2-3 tygodnie, koszt od 4-6 tys. zł** w zależności od skali katalogu.`,
  },

  // ============================================
  // 4. Koszty wdrożenia BaseLinker
  // ============================================
  {
    slug: "baselinker-koszty-wdrozenia-2026",
    title: "Baselinker — koszty wdrożenia 2026 (cennik realny)",
    metaDescription:
      "Ile naprawdę kosztuje wdrożenie Baselinkera? Pełny cennik 2026: licencje, integracje, automatyzacje, ERP. Realne kwoty z rynku, bez ściemy.",
    excerpt:
      "Cennik Baselinkera + koszt wdrożenia + koszty integracji. Pełna kalkulacja: ile zapłacisz za prosty sklep, średni i rozbudowany. Bez ukrytych opłat.",
    category: "Baselinker",
    publishedAt: PL_DATE_2026_04_02,
    readingTime: 10,
    keywords: ["baselinker cena wdrożenia", "baselinker koszt", "ile kosztuje baselinker"],
    ogTitle: "Baselinker — realne koszty wdrożenia 2026",
    ogDescription: "Cennik, koszty integracji i wdrożenia bez ściemy. Sprawdź, ile naprawdę kosztuje uruchomienie Baselinkera.",
    toc: [
      { id: "licencja", label: "Cennik licencji Baselinker" },
      { id: "wdrozenie", label: "Koszt wdrożenia od specjalisty" },
      { id: "scenariusze", label: "3 scenariusze cenowe" },
      { id: "ukryte", label: "Ukryte koszty, o których nie mówią" },
      { id: "roi", label: "Kiedy się zwraca" },
      { id: "oszczedzanie", label: "Jak oszczędzać przy wdrożeniu" },
    ],
    related: ["jak-polaczyc-baselinker-z-shopify", "automatyzacja-zamowien-baselinker", "migracja-z-shopgold-do-baselinker"],
    content: `## {licencja} Cennik licencji Baselinker (2026)

Baselinker rozlicza się **abonamentem miesięcznym** zależnym od liczby zamówień, nie liczby integracji. Aktualny cennik:

| Plan | Liczba zamówień / mies. | Cena (PLN/mies.) |
|------|------------------------|------------------|
| BASIC | do 100 | 39 zł |
| LITE | do 1 000 | 109 zł |
| START | do 3 000 | 199 zł |
| ENTERPRISE | do 10 000 | 449 zł |
| ENTERPRISE+ | 10 000+ | indywidualnie |

> **Ważne:** ceny dotyczą samej licencji. **Nie obejmują wdrożenia, integracji ani konfiguracji.**

W praktyce większość rosnących sklepów ląduje na planach START lub ENTERPRISE. **BASIC i LITE** są ekonomicznie sensowne tylko jako start dla małego sklepu.

## {wdrozenie} Koszt wdrożenia od specjalisty

Tu właśnie zaczyna się gra. **Sam Baselinker bez wdrożenia nie zadziała tak, jak sobie wyobrażasz.** Konieczna jest konfiguracja integracji, automatyzacji, mapowań i scenariuszy.

### Stawki na rynku (2026)

- **Specjalista samodzielny (jak ja):** 200-350 zł/h, projekt rozliczany ryczałtem
- **Mała agencja (2-5 osób):** 250-400 zł/h
- **Większa agencja (10+ osób):** 350-600 zł/h
- **Tańsi freelancerzy z OLX/Useme:** 80-150 zł/h — **uważaj na jakość**

### Standardowe pakiety wdrożeniowe

Większość specjalistów oferuje pakiety, nie godziny. Realne ceny:

- **Pakiet BASIC** (1 sklep + 1 marketplace + 2 kurierów): **3-5 tys. zł**
- **Pakiet STANDARD** (1 sklep + 3 marketplace'y + integracje + automatyzacje): **6-12 tys. zł**
- **Pakiet PRO** (multi-sklep + ERP + zaawansowane scenariusze): **15-30 tys. zł**
- **Pakiet ENTERPRISE** (custom integracje + middleware + raportowanie): **30-80 tys. zł**

## {scenariusze} 3 scenariusze cenowe

### Scenariusz 1: Mały sklep startujący

- **Profil:** sklep Shoper, 200 zamówień/mies., Allegro + InPost
- **Licencja Baselinker:** 109 zł/mies. (Lite)
- **Wdrożenie jednorazowe:** 4 000 zł
- **Łączny koszt 12 mies.:** ~5 300 zł
- **Czas wdrożenia:** 5-7 dni roboczych

### Scenariusz 2: Średni sklep w fazie wzrostu

- **Profil:** sklep Shopify, 1 500 zamówień/mies., Allegro + Amazon + 3 kurierów + Comarch ERP
- **Licencja Baselinker:** 199 zł/mies. (Start)
- **Wdrożenie jednorazowe:** 9 000 zł
- **Łączny koszt 12 mies.:** ~11 400 zł
- **Czas wdrożenia:** 3 tygodnie

### Scenariusz 3: Duży gracz

- **Profil:** multi-sklep (Shopify + ShopGold), 5 000 zamówień/mies., 5 marketplace'ów, własne middleware do raportowania
- **Licencja Baselinker:** 449 zł/mies. (Enterprise)
- **Wdrożenie jednorazowe:** 25 000 zł
- **Łączny koszt 12 mies.:** ~30 400 zł
- **Czas wdrożenia:** 6-8 tygodni

## {ukryte} Ukryte koszty, o których nie mówią

### 1. Płatne moduły dodatkowe

Niektóre integracje w Baselinkerze są płatne (np. zaawansowane raportowanie, niektóre kanały sprzedaży). Średnio **50-200 zł/mies.** dodatkowo.

### 2. Koszty integracji z ERP

Comarch, Subiekt, WAPRO często wymagają **dodatkowych licencji** lub modułów po swojej stronie. Budżet: **1-5 tys. zł** dodatkowo.

### 3. Wsparcie po wdrożeniu

Każda integracja wymaga utrzymania. Standardowy abonament wsparcia: **500-2000 zł/mies.** lub stawka godzinowa za realne zlecenia.

### 4. Custom development

Jeśli potrzebujesz funkcji, której Baselinker nie ma natywnie (np. specyficzny raport, custom workflow), potrzebny jest deweloper. **2-10 tys. zł** za projekt.

## {roi} Kiedy się zwraca

Standardowy ROI z wdrożenia Baselinkera:

- **Sklep z 200 zam./mies.:** zwrot w ~6-9 miesięcy (głównie przez oszczędność czasu).
- **Sklep z 1 500 zam./mies.:** zwrot w ~3-4 miesiące.
- **Sklep z 5 000+ zam./mies.:** zwrot w ~1-2 miesiące + wzrost obrotu o 15-30% przez automatyzacje marketingowe.

> **Z czego bierze się ROI:**
> - oszczędność 4-8 godzin pracy operatora dziennie (~6-12 tys. zł/mies. w skali 200 zam.),
> - mniej błędów = mniej zwrotów = +1-3% marży,
> - szybsza obsługa = więcej recenzji = wyższa konwersja na marketplace'ach.

## {oszczedzanie} Jak oszczędzać przy wdrożeniu

1. **Faza 1 minimum** — uruchom najpierw najważniejsze: sklep + 1 marketplace + 1 kurier. Resztę dorób stopniowo.
2. **Standardowe automatyzacje** — nie wymyślaj custom procesów na start. Użyj scenariuszy z biblioteki Baselinkera.
3. **Pomiń integrację z ERP w fazie 1** — to najdroższy element. Zostaw na później, gdy reszta będzie działać.
4. **Negocjuj rozliczenie ryczałtem** — godzinowe rozliczanie u agencji potrafi zaskoczyć. Lepiej ustalić budżet z góry.
5. **Pytaj o pakiety lojalnościowe** — wielu specjalistów daje 10-20% zniżki przy umowie 12-miesięcznej.

---

**Realna prognoza dla typowego sklepu (1500 zam./mies.):** budżet 10-15 tys. zł na wdrożenie + 200 zł/mies. na licencję = inwestycja, która zwraca się w 4-5 miesięcy i daje fundament do wzrostu.

Chcesz konkretną wycenę pod swój sklep? **Bezpłatna konsultacja, konkretne kwoty, brak narzutów na "obsługę projektu" i "koordynację" jak w korpo-agencjach.**`,
  },

  // ============================================
  // 5. Automatyzacje BaseLinker
  // ============================================
  {
    slug: "automatyzacja-zamowien-baselinker",
    title: "Automatyzacja zamówień w Baselinker — 10 scenariuszy z praktyki",
    metaDescription:
      "10 sprawdzonych automatyzacji w Baselinkerze, które oszczędzają godziny pracy dziennie: statusy, faktury, wysyłki, alerty. Z gotowymi scenariuszami do skopiowania.",
    excerpt:
      "Baselinker bez automatyzacji to jak Ferrari bez silnika. Pokazuję 10 konkretnych scenariuszy, które wdrażam u 90% klientów — i dlaczego działają.",
    category: "Baselinker",
    publishedAt: PL_DATE_2026_04_08,
    readingTime: 13,
    keywords: ["baselinker automatyzacje", "baselinker scenariusze", "baselinker akcje automatyczne"],
    ogTitle: "Automatyzacja zamówień w Baselinker — 10 scenariuszy",
    ogDescription: "Reguły, akcje i statusy z praktyki wdrożeń. 10 gotowych automatyzacji, które oszczędzają godziny pracy dziennie.",
    toc: [
      { id: "wstep", label: "Dlaczego automatyzacje to ROI #1" },
      { id: "1-status", label: "1. Auto-zmiana statusu po opłaceniu" },
      { id: "2-list", label: "2. Auto-list przewozowy" },
      { id: "3-tracking", label: "3. Auto-mail z trackingiem" },
      { id: "4-faktura", label: "4. Auto-faktura" },
      { id: "5-magazyn", label: "5. Auto-sync magazynu" },
      { id: "6-alerty", label: "6. Alerty niskiego stanu" },
      { id: "7-vip", label: "7. Oznaczanie klientów VIP" },
      { id: "8-gratis", label: "8. Auto-gratis powyżej kwoty" },
      { id: "9-zwroty", label: "9. Auto-obsługa zwrotów" },
      { id: "10-raport", label: "10. Codzienny raport KPI" },
    ],
    related: ["jak-polaczyc-baselinker-z-shopify", "baselinker-koszty-wdrozenia-2026", "integracja-sellasist-allegro-krok-po-kroku"],
    content: `## {wstep} Dlaczego automatyzacje to ROI #1

Baselinker bez automatyzacji to **drogi notes na zamówienia**. Cała wartość systemu pojawia się dopiero, gdy włączysz scenariusze, które robią pracę za Ciebie.

W ciągu 5 lat wdrożeń wyłoniłem **10 automatyzacji**, które wdrażam u praktycznie każdego klienta. Każda z nich rozwiązuje konkretny problem i oszczędza realne godziny.

## {1-status} 1. Auto-zmiana statusu po opłaceniu

**Problem:** zamówienia czekają w statusie "Nowe" aż ktoś je sprawdzi i przeniesie do "W realizacji".

**Scenariusz:**

- **Trigger:** zmiana statusu w sklepie na "Opłacone" / webhook od bramki płatniczej
- **Akcja:** ustaw status w Baselinkerze na "W realizacji"
- **Dodatkowo:** wyślij wewnętrzne powiadomienie do magazynu

**Oszczędność:** 1-2 minuty na zamówienie × 200 zamówień = **3-6 godzin dziennie**.

## {2-list} 2. Auto-list przewozowy po wybraniu kuriera

**Problem:** operator generuje listy ręcznie, klikając kilka razy w panelu kuriera.

**Scenariusz:**

- **Trigger:** zmiana statusu na "Pakowanie zakończone" + wybór kuriera
- **Akcja:** wygeneruj list przewozowy → drukuj na drukarce sieciowej → zmień status na "Gotowe do wysyłki"

**Oszczędność:** 30-60 sekund na zamówienie + 0 błędów w adresach.

## {3-tracking} 3. Auto-mail z numerem trackingu

**Problem:** klient pisze "Gdzie moja paczka?", obsługa odpowiada ręcznie.

**Scenariusz:**

- **Trigger:** kurier nadał paczkę (status "W drodze")
- **Akcja:** wyślij do klienta szablon maila z numerem trackingu i linkiem do śledzenia

**Efekt:** -30-50% zapytań do supportu o status paczki. **Klient czuje się zaopiekowany.**

## {4-faktura} 4. Auto-faktura po wysyłce

**Problem:** księgowa pyta co tydzień "gdzie faktury z zeszłego tygodnia?".

**Scenariusz:**

- **Trigger:** zmiana statusu na "Wysłane"
- **Akcja:** wystaw fakturę → wyślij PDF do klienta → wyślij dane do księgowości (np. JPK lub Comarch)

**Oszczędność:** +pewność, że ŻADNA sprzedaż nie umyka księgowości.

## {5-magazyn} 5. Auto-synchronizacja magazynu między kanałami

**Problem:** sprzedajesz na Allegro i w sklepie, stan się rozjeżdża, klienci kupują niedostępne produkty.

**Scenariusz:**

- **Trigger:** zmiana stanu w Baselinkerze (sprzedaż lub korekta)
- **Akcja:** zaktualizuj stan w sklepie + na wszystkich aktywnych marketplace'ach **w czasie <30 sekund**

**Efekt:** 0 oversellingu = 0 zwrotów z tego tytułu = 0 negatywnych opinii.

## {6-alerty} 6. Alerty niskiego stanu magazynowego

**Problem:** dowiadujesz się, że produkt się skończył, gdy klient już nie może go kupić.

**Scenariusz:**

- **Trigger:** stan produktu spadł poniżej zdefiniowanego progu (np. 10 szt.)
- **Akcja:** wyślij maila / Slack / SMS do osoby odpowiedzialnej za zakupy

**Efekt:** zamówienia od dostawców na czas, brak luk w sprzedaży.

## {7-vip} 7. Oznaczanie klientów VIP i priorytet obsługi

**Problem:** wszystkie zamówienia traktowane są jednakowo, klienci VIP nie mają priorytetu.

**Scenariusz:**

- **Trigger:** klient ma 10+ zamówień historycznych LUB zamówienie powyżej 1000 zł
- **Akcja:** dodaj tag "VIP" do zamówienia → priorytet w kolejce obsługi → automatyczny email od menedżera z podziękowaniem

**Efekt:** wzrost LTV klientów premium, więcej recenzji 5-gwiazdkowych.

## {8-gratis} 8. Automatyczny gratis powyżej kwoty

**Problem:** klienci nie wiedzą, że mogą dostać gratis, AOV nie rośnie.

**Scenariusz:**

- **Trigger:** wartość zamówienia > 300 zł
- **Akcja:** dodaj automatycznie produkt "Gratis_300" do zamówienia + dopisz notatkę dla magazynu

**Efekt:** wzrost AOV o 15-25%, klienci czują dodatkową wartość.

## {9-zwroty} 9. Automatyczna obsługa procesu zwrotu

**Problem:** zwroty obsługiwane manualnie zajmują 10-15 minut na sztukę.

**Scenariusz:**

- **Trigger:** klient wypełnił formularz zwrotu w sklepie
- **Akcja:** utwórz zlecenie zwrotu w Baselinkerze → wygeneruj etykietę zwrotną → wyślij do klienta → po otrzymaniu paczki: zwrot środków przez bramkę

**Oszczędność:** 10 min × 50 zwrotów/mies. = **8 godzin miesięcznie**.

## {10-raport} 10. Codzienny raport KPI na maila

**Problem:** musisz codziennie logować się do Baselinkera, żeby zobaczyć wczorajszą sprzedaż.

**Scenariusz:**

- **Trigger:** codziennie o 8:00
- **Akcja:** wygeneruj raport: liczba zamówień / wartość / podział na kanały / TOP 10 produktów / problemy → wyślij na maila / Slacka

**Efekt:** masz **kontrolę bez logowania się**, zauważasz problemy zanim eskalują.

---

**Suma oszczędności** dla średniego sklepu (200 zam./mies.) z wdrożonymi wszystkimi 10 automatyzacjami:

- **5-8 godzin pracy dziennie** zaoszczędzone (= 1-2 etaty),
- **30-50% mniej zapytań do supportu**,
- **15-25% wyższe AOV** dzięki automatyzacjom marketingowym,
- **0 oversellingu**, 0 błędów w adresach, 0 zapomnianych faktur.

To realny ROI Baselinkera. Sama platforma jest tylko narzędziem — wartość tworzą scenariusze.

Chcesz mieć te 10 automatyzacji wdrożone w swoim sklepie? **2 tygodnie pracy, ~6-8 tys. zł, ROI w 3-5 miesięcy.** Odezwij się.`,
  },

  // ============================================
  // 6. Wybór kuriera
  // ============================================
  {
    slug: "jak-wybrac-kuriera-dla-sklepu-internetowego",
    title: "Jak wybrać kuriera dla sklepu internetowego — porównanie 2026",
    metaDescription:
      "InPost, DPD, DHL, GLS, Pocztex — który kurier dla sklepu? Porównanie cen, czasów dostawy, integracji i jakości obsługi. Praktyczny przewodnik dla e-commerce.",
    excerpt:
      "Wybór kuriera ma większy wpływ na konwersję niż myślisz. Porównuję 5 największych firm kurierskich w Polsce pod kątem e-commerce — z konkretnymi rekomendacjami.",
    category: "Logistyka",
    publishedAt: PL_DATE_2026_04_12,
    readingTime: 11,
    keywords: ["wybór kuriera sklep internetowy", "integracja kurier ecommerce", "porównanie kurierów"],
    ogTitle: "Jak wybrać kuriera dla sklepu — porównanie 2026",
    ogDescription: "InPost, DPD, DHL, GLS, UPS — ceny, SLA i integracje. Praktyczne porównanie kurierów dla e-commerce.",
    toc: [
      { id: "wprowadzenie", label: "Dlaczego wybór kuriera jest kluczowy" },
      { id: "inpost", label: "InPost — Paczkomaty + Kurier" },
      { id: "dpd", label: "DPD" },
      { id: "dhl", label: "DHL" },
      { id: "gls", label: "GLS" },
      { id: "pocztex", label: "Pocztex" },
      { id: "rekomendacje", label: "Rekomendacje per profil sklepu" },
      { id: "integracje", label: "Integracje techniczne" },
    ],
    related: ["automatyzacja-zamowien-baselinker", "jak-polaczyc-baselinker-z-shopify", "integracja-sellasist-allegro-krok-po-kroku"],
    content: `## {wprowadzenie} Dlaczego wybór kuriera jest kluczowy

Wybór kuriera wpływa na **3 kluczowe metryki** Twojego sklepu:

1. **Konwersję** — klient widzi opcje dostawy w koszyku. Jeśli nie ma jego ulubionej (np. Paczkomat), porzuca koszyk.
2. **Marżę** — różnica między najtańszym a najdroższym kurierem to 5-15 zł na paczkę × 1000 paczek = 5-15 tys. zł miesięcznie.
3. **Reklamacje** — kurierzy z gorszą jakością generują więcej zniszczeń, opóźnień i konfliktów z klientami.

> **Brak Paczkomatu w opcjach dostawy** to obecnie konwersjowy samobój w 80% segmentów polskiego e-commerce.

## {inpost} InPost — Paczkomaty + Kurier

**Dla kogo:** każdy polski sklep B2C.

### Plusy

- **Paczkomaty są standardem** — 60-70% klientów wybiera je jako preferowaną opcję,
- najlepsza siec punktów odbioru w Polsce (~25 tys. paczkomatów),
- relatywnie niskie ceny dla średnich i dużych klientów,
- aplikacja InPost Mobile bardzo lubiana przez klientów,
- szybka i stabilna integracja z Baselinkerem.

### Minusy

- ceny dla małych klientów (do 500 paczek/mies.) są wyższe niż konkurencji,
- zwroty z paczkomatów wymagają dodatkowej konfiguracji,
- jakość obsługi reklamacji średnia.

**Koszt orientacyjny:** 9-12 zł za paczkomat, 14-18 zł za kuriera (przy umowie B2B).

## {dpd} DPD

**Dla kogo:** sklepy B2C i B2B z większymi paczkami, sprzedaż międzynarodowa.

### Plusy

- doskonała sieć punktów DPD Pickup,
- dobra obsługa paczek o nietypowych wymiarach,
- szeroka sieć międzynarodowa (cała UE),
- aplikacja DPD do śledzenia paczek bardzo dobra.

### Minusy

- ceny wyższe niż InPost przy małych paczkach,
- czas dostawy 1-2 dni (rzadko D+1, częściej D+2).

**Koszt orientacyjny:** 12-16 zł za Pickup, 16-22 zł za kuriera.

## {dhl} DHL

**Dla kogo:** sklepy z luxury/premium, B2B, sprzedaż międzynarodowa.

### Plusy

- najwyższa jakość obsługi (mało zniszczeń, mało opóźnień),
- najlepsza opcja dla wysyłek międzynarodowych poza UE,
- DHL Parcelshop coraz lepszą siecią punktów,
- dobry dla paczek wysokiej wartości (lepsze ubezpieczenia).

### Minusy

- najdroższy z dużych kurierów,
- dla małych sklepów (<200 paczek/mies.) trudno wynegocjować dobrą umowę.

**Koszt orientacyjny:** 14-18 zł za Parcelshop, 18-25 zł za kuriera.

## {gls} GLS

**Dla kogo:** średnie sklepy szukające bilansu cena/jakość.

### Plusy

- dobre ceny dla średnich klientów (200-1000 paczek/mies.),
- stabilna jakość, mało zniszczeń,
- Sieć punktów GLS Parcel Shop rozsądnej wielkości,
- bardzo dobre integracje techniczne (proste API).

### Minusy

- mniejsza rozpoznawalność marki niż InPost/DPD/DHL,
- mniej "fan-base" wśród klientów (rzadko wybierany jako default).

**Koszt orientacyjny:** 11-15 zł za punkt, 15-19 zł za kuriera.

## {pocztex} Pocztex

**Dla kogo:** sklepy obsługujące mniejsze miasta i wsie, gdzie inne firmy mają słabe pokrycie.

### Plusy

- najlepsza dostępność w mniejszych miejscowościach,
- dostawa do każdego adresu w Polsce,
- niskie ceny dla niskich gabarytów,
- bardzo dobra integracja z punktami Poczty Polskiej.

### Minusy

- jakość niestabilna (czasami opóźnienia),
- ślad wizerunkowy "Poczty Polskiej" — niektórzy klienci go nie lubią,
- Tracking nie zawsze aktualny.

**Koszt orientacyjny:** 8-12 zł za punkt, 12-16 zł za kuriera.

## {rekomendacje} Rekomendacje per profil sklepu

### Mały sklep B2C (do 500 paczek/mies.)

**Default:** InPost Paczkomaty + InPost Kurier.
**Backup:** DPD lub Pocztex jako druga opcja w koszyku.

### Średni sklep B2C (500-3000 paczek/mies.)

**Mix:** InPost Paczkomaty + DPD Pickup + InPost Kurier (na odbiory pod adres).
**Negocjuj:** umowę indywidualną z każdym z trzech.

### Duży sklep B2C/B2B (3000+ paczek/mies.)

**Mix wszystkich:** InPost + DPD + GLS + DHL (dla premium) + Pocztex (dla małych miejscowości).
**Reguły automatyczne** w Baselinkerze: wybierz najtańszego kuriera dla danego adresu/wagi.

### Sklep premium / luxury

**Default:** DHL.
**Komunikacja:** podkreślaj jakość obsługi i ubezpieczenie. Klient premium płaci za jakość, nie za cenę.

### Sklep międzynarodowy

**UE:** DPD lub GLS.
**Poza UE:** DHL.

## {integracje} Integracje techniczne

Z perspektywy Baselinkera **wszystkie 5 firm ma natywne integracje** — nie ma problemu z wpięciem żadnej z nich. Różnice są w detalach:

- **InPost:** najszybsza integracja, najwięcej opcji konfiguracji,
- **DPD:** stabilna, dobre webhooki,
- **DHL:** czasem powolne API, ale stabilne,
- **GLS:** najprostsze i najczystsze API,
- **Pocztex:** API bywa mniej stabilne, czasami wymaga retry.

> **Rada:** nigdy nie zostawaj na 1 kurierze. Mix 2-3 firm = redundancja w razie awarii API jednego z nich.

---

**Wybór kuriera nie jest decyzją na lata** — można go zmieniać iteracyjnie. Ale dobry mix od początku oszczędza pieniądze i nerwy.

Jeśli chcesz, żebym **wynegocjował dla Ciebie umowy z kurierami** + skonfigurował integracje + ustawił reguły wyboru kuriera w Baselinkerze — odezwij się. Standardowy projekt: 1-2 tygodnie, koszt zwykle zwraca się w pierwszych 2-3 miesiącach niższych cen wysyłki.`,
  },

  // ============================================
  // 7. Google Ads dla e-commerce
  // ============================================
  {
    slug: "google-ads-dla-ecommerce-przewodnik-2026",
    title: "Google Ads dla e-commerce — przewodnik 2026 (Performance Max, Shopping)",
    metaDescription:
      "Jak prowadzić skuteczne kampanie Google Ads dla sklepu internetowego. Performance Max, Shopping, Search — strategia, konfiguracja, ROAS. Przewodnik 2026.",
    excerpt:
      "Google Ads to fundament płatnego ruchu w e-commerce. Pokazuję, jak ułożyć kampanie, żeby ROAS rósł, a budżet się skalował — z konkretnymi liczbami z praktyki.",
    category: "Marketing",
    publishedAt: PL_DATE_2026_04_16,
    readingTime: 13,
    keywords: ["google ads sklep internetowy", "performance max ecommerce", "google shopping kampanie"],
    ogTitle: "Google Ads dla e-commerce — przewodnik 2026",
    ogDescription: "Performance Max, Shopping i konfiguracja konwersji. Jak skutecznie reklamować sklep w Google w 2026.",
    toc: [
      { id: "fundamenty", label: "Fundamenty kampanii e-commerce" },
      { id: "merchant", label: "Google Merchant Center i feed" },
      { id: "pmax", label: "Performance Max — koń pociągowy" },
      { id: "shopping", label: "Standard Shopping — kontrola i transparentność" },
      { id: "search", label: "Search — brand + competitors" },
      { id: "remarketing", label: "Remarketing i Display" },
      { id: "tracking", label: "Tracking i atrybucja" },
      { id: "skalowanie", label: "Skalowanie budżetu" },
    ],
    related: ["facebook-conversions-api-wdrozenie-krok-po-kroku", "automatyzacja-zamowien-baselinker", "integracja-sellasist-allegro-krok-po-kroku"],
    content: `## {fundamenty} Fundamenty kampanii e-commerce

Google Ads dla e-commerce w 2026 stoi na **pięciu filarach**:

1. **Merchant Center + feed produktowy** — bez tego nic nie polecisz.
2. **Performance Max** — główna kampania zakupowa.
3. **Standard Shopping** — uzupełnienie i kontrola.
4. **Search Brand + Competitors** — obrona marki + atak na konkurencję.
5. **Remarketing** — odzyskiwanie odwiedzających.

> **Najważniejsza zasada:** budżet idzie w kanały **mierzalne**. Bez poprawnego trackingu (GA4 + conversion actions + Conversions API) wszystko inne nie ma znaczenia.

## {merchant} Google Merchant Center i feed produktowy

To **fundament wszystkich kampanii e-commerce w Google**. Bez Merchant Center nie uruchomisz Performance Max ani Shoppinga.

### Konfiguracja

1. **Załóż konto Merchant Center** — bezpłatne, na merchantcenter.google.com.
2. **Zweryfikuj domenę sklepu** — przez Search Console lub plik HTML.
3. **Skonfiguruj feed produktowy** — najlepiej automatyczny z platformy sklepowej (Shopify, Shoper, ShopGold, PrestaShop mają natywne moduły).
4. **Połącz Merchant Center z Google Ads** — w ustawieniach obu kont.

### Najczęstsze błędy w feedzie

- **Brak GTIN/EAN** — produkty bez kodu kreskowego mają niższą widoczność.
- **Niepełne tytuły** — tytuł powinien zawierać markę + model + kluczowy atrybut (kolor, rozmiar).
- **Słabe zdjęcia** — białe tło, dobra rozdzielczość, produkt zajmuje 75-90% kadru.
- **Brakujące atrybuty** — zwłaszcza brand, size, color, gender, age_group.

### Diagnostic w Merchant Center

Wszystkie błędy zobaczysz w **zakładce Diagnostic**. Najczęstsze:

- "Image too small" — wymień zdjęcia na min. 800×800 px,
- "Missing GTIN" — uzupełnij EAN (jeśli nie masz, oznacz \`identifier_exists: false\`),
- "Price mismatch" — cena w feedzie nie zgadza się z ceną na stronie produktu,
- "Insufficient product information" — uzupełnij brak atrybutów.

## {pmax} Performance Max — koń pociągowy

PMax to **algorytmiczna kampania**, która dystrybuuje budżet po wszystkich kanałach Google (Shopping, Display, YouTube, Discover, Gmail) automatycznie.

### Kiedy warto

- Masz **conversion tracking ustawiony poprawnie** (bez tego PMax nie ma na czym się uczyć).
- Budżet **min. 100-200 zł dziennie** na kampanię (poniżej algorytm nie ma danych).
- Masz **historię konwersji** (min. 30-50 konwersji w ostatnich 30 dniach) — start od zera jest możliwy, ale uczenie potrwa 4-6 tygodni.

### Konfiguracja

1. **Cel:** Sales (Maximize conversions value).
2. **Asset Group:** podziel po kategoriach produktowych (osobny asset group na "Buty", osobny na "Akcesoria").
3. **Listings (z Merchant Center):** włącz wszystkie produkty albo wybrane kategorie.
4. **Audiences:** dodaj **signal audiences** — Twoje listy klientów, lookalike, custom audiences.
5. **Budżet:** start 200-500 zł/dzień, skalowanie po 2 tygodniach.

### Optymalizacja

PMax nie daje pełnej transparentności (Google chowa szczegóły). Twoje narzędzia:

- **Asset Group performance** — które grupy produktowe działają najlepiej,
- **Insights** — raporty trendów wyszukiwania,
- **Listing Group exclusions** — wyłącz produkty z niską marżą.

## {shopping} Standard Shopping — kontrola i transparentność

PMax jest świetne, ale czasami chcesz **pełnej kontroli**. Wtedy uruchamiasz Standard Shopping (manualne CPC).

### Kiedy warto

- Masz produkty z bardzo różną marżą (chcesz różne CPC dla różnych produktów).
- Chcesz testować **konkretne strategie cenowe** (np. dropshipping vs własny magazyn).
- PMax nie dowozi ROAS na konkretnej kategorii — Standard Shopping pomaga zidentyfikować problem.

### Struktura

- **Kampania per kategoria** (Buty, Odzież, Akcesoria).
- **Ad Group per typ produktu** (Buty zimowe, Buty letnie).
- **Bid per produkt** (najwyższy ROI = najwyższy CPC).

## {search} Search — brand + competitors

Search to obrona i atak.

### Brand Campaign

**Cel:** wykupić własną markę. Klient szuka "synctech" i widzi Twój ad pierwszy. Konkurencja nie może podbić Twojej marki tanio.

- **Słowa kluczowe:** Twoja marka + warianty.
- **Budżet:** niski, ale stały.
- **CPC:** zwykle 0.5-2 zł, bardzo wysoki ROAS.

### Competitors Campaign

**Cel:** podbić markę konkurencji. Klient szuka "konkurent X", a Ty wyświetlasz reklamę "Lepsza alternatywa dla X".

- **Ostrożnie z polityką Google** — nie używaj nazwy konkurencji w treści reklamy, tylko jako keyword.
- **CPC:** wyższe niż brand (1-5 zł), ale potrafi dawać dobry ROAS, jeśli oferujesz coś unikalnego.

## {remarketing} Remarketing i Display

Remarketing to **najtańszy ROAS w Google Ads** dla e-commerce.

### Listy remarketingowe (z GA4 lub Google Ads)

- **Wszyscy odwiedzający** — remarketing ogólny (Display Network).
- **Odwiedzający kartę produktu, nie kupili** — remarketing produktowy z dynamicznymi adami.
- **Porzucony koszyk** — najwyższy ROAS, agresywne CPC.
- **Klienci z 90 dni** — cross-sell innych produktów.
- **Klienci > 1 rok** — win-back z atrakcyjnym kuponem.

### Dynamic Remarketing

Reklama pokazuje **dokładnie te produkty**, które klient oglądał. Wymaga poprawnego feedu Merchant Center + Pixel/Tag z product IDs.

## {tracking} Tracking i atrybucja

Bez trackingu **wszystko inne to teatr**. W 2026 standard to:

1. **GA4 z Enhanced Ecommerce** — view_item, add_to_cart, begin_checkout, purchase z wartością i walutą.
2. **Conversion Actions w Google Ads** — purchase jako primary, leady i mikro-konwersje jako secondary.
3. **Conversions API server-side** (Google Ads Enhanced Conversions for Web + Server) — odzyskuje dane utracone przez ad-blockery i restrykcje przeglądarek.
4. **Cross-domain tracking** — jeśli checkout jest na innej subdomenie (np. checkout.sklep.pl), musi być spięty.
5. **Atrybucja Data-Driven** (domyślna w GA4) — bardziej realistyczna niż last-click.

> **Bez Conversions API tracisz 20-40% danych** od momentu wprowadzenia iOS 14 i restrykcji Safari/Firefox.

## {skalowanie} Skalowanie budżetu

Skalowanie = **podnoszenie budżetu bez utraty ROAS**.

### Zasady skalowania

1. **Powoli** — max +20% budżetu na tydzień. Algorytmy potrzebują czasu na przeuczenie.
2. **Mierz ROAS przyrostowy** — czy +1000 zł budżetu daje +X przychodu i jakim ROAS.
3. **Nie skaluj, gdy ROAS spada** — najpierw zoptymalizuj, potem skaluj.
4. **Dywersyfikuj** — gdy 1 kanał osiągnie sufit, otwórz drugi (np. Facebook Ads obok Google).
5. **Sezony** — przed świętami skaluj agresywniej, po świętach redukuj.

### Realny case skalowania

- **Start:** budżet 5 tys. zł/mies., ROAS 4.2.
- **3 miesiące:** budżet 15 tys. zł/mies., ROAS 3.8 (lekki spadek przy skalowaniu).
- **6 miesięcy:** budżet 30 tys. zł/mies., ROAS 4.5 (po dodaniu PMax + remarketingu).
- **12 miesięcy:** budżet 60 tys. zł/mies., ROAS 4.2 (sufit kanału, otwieranie Facebook Ads).

---

**Google Ads dla e-commerce to maraton, nie sprint.** Pierwsze 2-3 miesiące to faza nauki algorytmów + optymalizacji. Realne wyniki przychodzą od 4. miesiąca.

Jeśli chcesz, żebym **prowadził Twoje kampanie Google Ads** — odezwij się. Pracuję na efekcie (% od wydatku reklamowego + bonus za przekroczenie celu ROAS), więc moje wyniki = Twoje wyniki.`,
  },

  // ============================================
  // 8. Conversions API
  // ============================================
  {
    slug: "facebook-conversions-api-wdrozenie-krok-po-kroku",
    title: "Facebook Conversions API — wdrożenie krok po kroku 2026",
    metaDescription:
      "Jak wdrożyć Facebook Conversions API server-side dla sklepu internetowego. Odzyskaj dane utracone przez iOS 14, ad-blockery i restrykcje przeglądarek.",
    excerpt:
      "Pixel sam już nie wystarcza. Pokazuję, jak wdrożyć Conversions API server-side i odzyskać 30-50% utraconych konwersji — z gotowymi krokami i kodem.",
    category: "Marketing",
    publishedAt: PL_DATE_2026_04_20,
    readingTime: 12,
    keywords: ["facebook conversions api", "conversions api wdrożenie", "fb capi server-side"],
    ogTitle: "Facebook Conversions API — wdrożenie krok po kroku",
    ogDescription: "Server-side tracking, deduplikacja zdarzeń i jakość danych. Pełen przewodnik wdrożenia FB CAPI w 2026.",
    toc: [
      { id: "dlaczego", label: "Dlaczego CAPI to konieczność" },
      { id: "jak-dziala", label: "Jak działa Conversions API" },
      { id: "wymagania", label: "Wymagania wstępne" },
      { id: "metody", label: "3 metody wdrożenia" },
      { id: "gtm-server", label: "GTM Server-side — najlepsza opcja" },
      { id: "events", label: "Jakie eventy wysyłać" },
      { id: "deduplication", label: "Deduplikacja Pixel + CAPI" },
      { id: "test", label: "Testowanie i debugowanie" },
    ],
    related: ["google-ads-dla-ecommerce-przewodnik-2026", "automatyzacja-zamowien-baselinker", "jak-polaczyc-baselinker-z-shopify"],
    content: `## {dlaczego} Dlaczego CAPI to konieczność w 2026

Tradycyjny **Facebook Pixel** to JavaScript działający w przeglądarce. Problem: w 2026 ponad **40% użytkowników jest dla Pixela niewidoczna** z powodu:

- **iOS 14+ App Tracking Transparency** — większość użytkowników iPhone'a wybrała "Ask app not to track",
- **Safari ITP** — Apple aktywnie blokuje cookies trackingowe,
- **Firefox Enhanced Tracking Protection** — domyślnie blokuje Pixel,
- **Ad-blockery** (uBlock, AdBlock Plus) — blokują skrypty Pixela,
- **Brave, DuckDuckGo** — przeglądarki z domyślną blokadą trackingu.

> **Skutek:** Twoje kampanie Facebook Ads optymalizują się na **danych niekompletnych**, kosztują więcej i mają niższy ROAS niż mogłyby.

**Conversions API rozwiązuje ten problem**, wysyłając zdarzenia konwersji **bezpośrednio z serwera** Twojego sklepu do Facebooka — z pominięciem przeglądarki, czyli z pominięciem ad-blockerów i restrykcji.

## {jak-dziala} Jak działa Conversions API

Tradycyjny przepływ z samym Pixelem:

\`\`\`
Klient → przeglądarka → Pixel (JS) → Facebook
            ❌ Ad-blocker / iOS 14 blokuje
\`\`\`

Przepływ z CAPI:

\`\`\`
Klient → przeglądarka → Pixel (JS) → Facebook
                ↓
            Twój serwer → CAPI → Facebook
            ✅ Niezależne od przeglądarki
\`\`\`

Po wdrożeniu CAPI Facebook **łączy oba sygnały** (Pixel + CAPI) w jeden, deduplikowany strumień danych. Dostajesz pełen obraz konwersji.

## {wymagania} Wymagania wstępne

- **Konto Facebook Business Manager** z dostępem do Pixela.
- **Sklep internetowy** z dostępem do kodu serwera (Shopify Plus, Shoper z modułami, ShopGold, własne rozwiązania).
- **HTTPS** na całej domenie (CAPI nie wyśle z HTTP).
- **Możliwość wysyłki webhooków server-side** (lub Tag Manager Server-side jako pośrednik).
- **Dostęp do CRM lub bazy zamówień** (do wzbogacenia danych użytkownika).

## {metody} 3 metody wdrożenia

### Metoda 1: Natywna integracja platformy

**Najprostsza opcja**, jeśli Twoja platforma ją wspiera:

- **Shopify:** wbudowane w Facebook & Instagram channel,
- **WooCommerce:** plugin "Facebook for WooCommerce",
- **Shoper:** moduł "Facebook Pixel + CAPI",
- **PrestaShop:** moduł "Facebook Conversion API".

**Plus:** instalacja w 30 minut.
**Minus:** ograniczona kontrola, brak custom eventów, słaba deduplikacja.

### Metoda 2: GTM Server-side (RECOMMENDED)

Google Tag Manager Server-side staje się **standardem branżowym**. Pełna kontrola, deduplikacja, łatwe debugowanie.

**Plus:** elastyczność, łączy CAPI dla Facebooka + Conversions API dla Google Ads + inne narzędzia,
**Minus:** wymaga GTM Cloud (koszt ~120 zł/mies.) + konfiguracji (1-3 dni).

### Metoda 3: Custom kod server-side

Bezpośrednio z backendu sklepu (Node.js, PHP, Python) wywołujesz Facebook Marketing API.

**Plus:** najtańsze, najprostsze technicznie dla developerów.
**Minus:** trudne w utrzymaniu, brak narzędzi debugowania, każdy dodatkowy event = praca developera.

## {gtm-server} GTM Server-side — najlepsza opcja

To podejście, które wdrażam u 90% klientów. Krok po kroku:

### Krok 1: Setup GTM Server Container

1. W tagmanager.google.com **utwórz nowy kontener Server-side**.
2. Wybierz Cloud (Google Cloud Run, ~$15-30/mies.) — najprostsze.
3. Skonfiguruj **subdomenę** (np. \`tracking.twojsklep.pl\`) wskazującą na server endpoint.
4. Doda **DNS A record** w panelu domeny.

### Krok 2: Konfiguracja Web Container (klient)

W kliencie GTM (przeglądarka):

1. **Edytuj zmienną Google Tag** — przekieruj transport na Twój endpoint server-side.
2. **Pixel Facebook** zostaje na kliencie (dla deduplikacji).
3. **Wszystkie eventy** (purchase, add_to_cart) wysyłane są **też** do server-side endpoint.

### Krok 3: Konfiguracja Server Container

W kontenerze server-side:

1. **Klient: GA4** (do odbioru eventów).
2. **Tag: Conversions API Facebook** (z templates community).
3. **Mapowanie pól:** event_name, event_id (do deduplikacji), user_data (email, phone — zhashowane), custom_data (value, currency, content_ids).

### Krok 4: User Data — wzbogacenie

Dla maksymalnego matchu użytkowników po stronie Facebooka, wysyłaj **maksimum danych użytkownika** (zhashowane SHA256):

- **email** — najważniejszy, daje 90%+ matchu,
- **phone** — drugi co do wagi,
- **first_name, last_name** — uzupełnienie,
- **city, zip, country** — geo,
- **external_id** — Twoje ID klienta w bazie,
- **fbp, fbc** — cookies Facebooka z przeglądarki.

> **Im więcej danych user_data, tym wyższy match rate** — celuj w 80%+.

## {events} Jakie eventy wysyłać

Standardowe eventy e-commerce (zarówno Pixel jak CAPI):

1. **PageView** — każda strona,
2. **ViewContent** — wejście na kartę produktu (z product_id, value),
3. **AddToCart** — dodanie do koszyka,
4. **InitiateCheckout** — wejście na checkout,
5. **AddPaymentInfo** — dodanie metody płatności,
6. **Purchase** — zakup (z value, currency, content_ids).

**Custom eventy** dla zaawansowanych:

- **Lead** — zapis na newsletter, pobranie lead magnetu,
- **CompleteRegistration** — założenie konta,
- **Search** — wyszukiwanie w sklepie.

## {deduplication} Deduplikacja Pixel + CAPI

Bez deduplikacji Facebook policzy każdą konwersję 2 razy (Pixel + CAPI). Aby tego uniknąć:

1. **Wygeneruj unikalny event_id** dla każdej konwersji (np. order_id w sklepie).
2. **Wyślij ten sam event_id** zarówno z Pixela jak z CAPI.
3. Facebook automatycznie zdedupliplikuje na podstawie event_id.

**Kod Pixel:**

\`\`\`javascript
fbq('track', 'Purchase', {
  value: 199.99,
  currency: 'PLN',
  content_ids: ['SKU123', 'SKU456'],
  content_type: 'product'
}, {
  eventID: 'order_12345' // <-- klucz deduplikacji
});
\`\`\`

**CAPI request (server-side):**

\`\`\`json
{
  "event_name": "Purchase",
  "event_id": "order_12345",
  "event_time": 1745432100,
  "user_data": { "em": "hashed_email", "ph": "hashed_phone" },
  "custom_data": { "value": 199.99, "currency": "PLN" }
}
\`\`\`

## {test} Testowanie i debugowanie

### Test Events Tool (Facebook)

W **Events Manager → Test Events** wpisz **test_event_code** i obserwuj eventy w czasie rzeczywistym. Sprawdzisz:

- czy eventy w ogóle przychodzą,
- czy match użytkownika działa (powinien być >80%),
- czy deduplikacja działa,
- czy wartości są poprawne.

### Event Match Quality (EMQ)

W Events Manager każdy event ma score EMQ od 0 do 10. Cel: **8.0+**. Jeśli niżej, wzbogać user_data.

### Diagnostic w Events Manager

Pokazuje błędy: brakujące pola, źle hashowane dane, problemy z deduplikacją.

---

**Po wdrożeniu CAPI** typowe efekty:

- **+20-40% mierzonych konwersji** (te utracone wcześniej przez Pixel),
- **Lepsza optymalizacja kampanii** (algorytm widzi więcej sygnałów),
- **+10-25% ROAS** w pierwszych 2-3 miesiącach (algorytm "uczy się" na pełnych danych),
- **Niższy CPA** średnio o 15-30%.

Wdrożenie CAPI to obecnie **standard, nie luxus**. Bez niego konkurujesz z handicapem przeciw firmom, które już to mają.

Chcesz, żebym wdrożył CAPI + GTM Server-side dla Twojego sklepu? **Standardowy projekt: 1-2 tygodnie, koszt 5-10 tys. zł, ROI w 2-4 miesiące.** Odezwij się.`,
  },

  // ============================================
  // 9. Integracja Baselinker z Allegro
  // ============================================
  {
    slug: "integracja-baselinker-allegro-przewodnik",
    title: "Integracja Baselinker z Allegro — pełen przewodnik 2026",
    metaDescription:
      "Jak skonfigurować integrację Baselinker z Allegro: konto SMART!, kategorie, parametry, wystawianie masowe, automatyzacje statusów i najczęstsze błędy.",
    ogTitle: "Baselinker × Allegro — przewodnik integracji 2026",
    ogDescription: "Wystawiaj masowo, mapuj parametry i automatyzuj statusy. Pełen przewodnik integracji Baselinker z Allegro.",
    ogImage: "https://synctech.pl/og-allegro-baselinker.jpg",
    excerpt:
      "Allegro to ponad 70% polskiego e-commerce. Pokazuję, jak połączyć je z Baselinkerem, żeby wystawiać szybciej, nie spamować zwrotami i odzyskać kontrolę nad cenami.",
    category: "Baselinker",
    publishedAt: PL_DATE_2026_04_25,
    readingTime: 12,
    keywords: ["baselinker allegro", "integracja baselinker allegro", "wystawianie allegro baselinker"],
    toc: [
      { id: "po-co", label: "Po co łączyć Baselinker z Allegro" },
      { id: "wymagania", label: "Wymagania wstępne" },
      { id: "konfiguracja", label: "Konfiguracja konta i połączenia" },
      { id: "kategorie", label: "Kategorie, parametry i szablony" },
      { id: "wystawianie", label: "Masowe wystawianie ofert" },
      { id: "automatyzacje", label: "Automatyzacje statusów i komunikacji" },
      { id: "bledy", label: "Najczęstsze błędy i jak ich unikać" },
      { id: "podsumowanie", label: "Podsumowanie" },
    ],
    related: ["integracja-sellasist-allegro-krok-po-kroku", "automatyzacja-zamowien-baselinker", "jak-polaczyc-baselinker-z-shopify"],
    faqs: [
      {
        q: "Ile trwa wdrożenie integracji Baselinker z Allegro?",
        a: "Standardowo 1-2 tygodnie pracy dla doświadczonego operatora: 2-3 dni na konfigurację konta, mapowanie kategorii i parametrów, kolejne dni na szablony, automatyzacje statusów i testy na próbce 20-50 ofert. Większe katalogi (5000+ ofert) potrafią zająć 3-4 tygodnie.",
      },
      {
        q: "Czy Baselinker obsługuje wiele kont sprzedawcy na Allegro?",
        a: "Tak, w jednym Baselinkerze możesz podłączyć dowolną liczbę kont Allegro (np. konto główne, outlet, B2B). Każde konto autoryzujesz osobno przez OAuth i przypisujesz mu własne szablony oraz zasady cenowe.",
      },
      {
        q: "Czy potrzebuję planu PRO w Baselinkerze do integracji z Allegro?",
        a: "Praktycznie tak. Plan Free ma zbyt niskie limity API i nie pozwoli na masowe wystawianie ani repricer przy realnej skali. Dla sklepu od 100 zamówień miesięcznie minimum to plan PRO.",
      },
      {
        q: "Czy repricer Baselinkera może mi zaszkodzić w wynikach Allegro?",
        a: "Może — jeśli włączysz go za wcześnie lub bez sensownych reguł. Algorytm Allegro karze za zbyt częste zmiany cen. Włącz repricer po 2-3 tygodniach stabilnej sprzedaży, z minimalną i maksymalną ceną oraz krokiem co najmniej 1-2 zł.",
      },
      {
        q: "Co zrobić, gdy oferty masowo zwracają błędy walidacji?",
        a: "99% przypadków to brak parametrów obowiązkowych (marka, EAN, stan) lub złe mapowanie kategorii (zbyt ogólna). Otwórz zakładkę Allegro → Błędy, popraw produkty w katalogu Baselinkera, ponów wystawienie. Nie próbuj poprawiać 500 ofert na raz — zacznij od 10.",
      },
    ],
    content: `## {po-co} Po co łączyć Baselinker z Allegro

Allegro samo w sobie pozwala wystawiać oferty i obsługiwać zamówienia, ale przy 100+ ofertach i kilku kanałach sprzedaży staje się wąskim gardłem. Baselinker rozwiązuje trzy konkretne problemy:

- **Centralizacja zamówień** — zamówienia z Allegro, sklepu i innych marketplace'ów lądują w jednym widoku (więcej w tekście o [automatyzacji zamówień w Baselinkerze](/blog/automatyzacja-zamowien-baselinker)).
- **Masowe wystawianie** — z jednego produktu generujesz dziesiątki ofert na różne kategorie i konta sprzedawcy.
- **Automatyczne statusy + tracking** — paczka wychodzi, status na Allegro zmienia się sam, klient dostaje numer śledzenia (np. z [InPost Paczkomatów](/blog/inpost-paczkomaty-integracja-baselinker)).

> **W praktyce:** sklep z 2000 ofertami na Allegro, który ręcznie aktualizował ceny i stany 3x dziennie, po wdrożeniu Baselinkera robi to w pełni automatycznie i odzyskuje 4 godziny pracy dziennie.

## {wymagania} Wymagania wstępne

Zanim klikniesz "połącz konto", upewnij się, że masz:

- **Konto sprzedawcy na Allegro** (Firma) z aktywnym abonamentem lub pakietem ofert.
- **Konto Baselinker** — minimum plan PRO ze względu na limity API (zobacz też [wdrożenia Baselinker](/platformy/baselinker) i [koszty wdrożenia 2026](/blog/baselinker-koszty-wdrozenia-2026)).
- **Uporządkowany katalog produktów** w Baselinkerze (kategorie, EAN, zdjęcia, opisy HTML).
- **Polityki sklepu na Allegro** — zwroty, reklamacje, dostawy. Bez nich oferty się nie wystawią.
- **Cenniki dostaw** zdefiniowane na Allegro (Allegro Smart!, paczkomaty, kurier).

## {konfiguracja} Konfiguracja konta i połączenia

W Baselinkerze otwórz **Integracje → Marketplace → Allegro → Dodaj konto**. Zostaniesz przekierowany na ekran logowania Allegro i poproszony o autoryzację OAuth. Bez tego kroku Baselinker nie będzie mógł wystawiać ofert ani pobierać zamówień.

### Co skonfigurować od razu po podłączeniu

1. **Synchronizacja zamówień** — co ile minut Baselinker pyta Allegro o nowe zamówienia. Standard: 5 minut.
2. **Domyślny status zamówienia** — np. "Nowe Allegro" (oddzielny od zamówień ze sklepu).
3. **Domyślny magazyn** — z którego schodzą stany po sprzedaży.
4. **Synchronizacja stanów magazynowych** — włącz w obie strony, ustaw bufor 1-2 szt.
5. **Mapowanie metod dostawy** — każdej metodzie z Allegro przypisz odpowiednik w Baselinkerze (to później automatyzuje generowanie listów przewozowych).

> **Praktyczna rada:** nie podłączaj od razu wszystkich kont sprzedawcy. Zacznij od jednego, przetestuj 10-20 zamówień, dopiero potem dodawaj kolejne.

## {kategorie} Kategorie, parametry i szablony

To miejsce, w którym najwięcej projektów się sypie. Allegro wymaga **kategorii + zestawu parametrów obowiązkowych** dla każdej oferty. Jeśli zrobisz to bez głowy, dostaniesz tysiące błędów walidacji przy wystawianiu.

### Krok 1: Mapowanie kategorii

W Baselinkerze otwórz **Katalog → Kategorie → Mapuj na Allegro**. Każdej swojej kategorii przypisz **najgłębszą** kategorię Allegro (nie ogólną — algorytm Allegro promuje dopasowane).

### Krok 2: Parametry

Dla każdej kategorii Allegro Baselinker pobiera listę parametrów (marka, kolor, rozmiar, EAN, stan). Część jest obowiązkowa, część opcjonalna. **Wypełnij wszystkie opcjonalne** — to wpływa na pozycję w wynikach Allegro.

### Krok 3: Szablon oferty

Stwórz **min. 2-3 szablony** (np. "Standard", "Premium z gwarancją", "Outlet"). Szablon definiuje:

- czas wysyłki,
- politykę zwrotów,
- cennik dostaw,
- nazwę firmy w stopce,
- domyślne parametry promocji (Smart, wyróżnienie).

Bez szablonów będziesz musiał ustawiać to ręcznie przy każdej ofercie.

## {wystawianie} Masowe wystawianie ofert

Najmocniejsza funkcja połączenia Baselinker + Allegro. Z jednego produktu w katalogu generujesz dziesiątki ofert:

1. Zaznaczasz produkty w katalogu (np. 200 sztuk).
2. **Akcje masowe → Wystaw na Allegro**.
3. Wybierasz konto sprzedawcy + szablon + kategorię.
4. Baselinker przelicza ceny (możesz dodać marżę procentową lub kwotową), pobiera parametry i wystawia oferty kolejką.

> **Bezpiecznik:** zacznij od 10 ofert testowych. Jeśli się wystawią poprawnie, wystawiaj kolejne 50, potem 200, potem resztę. Nie wystawiaj 2000 ofert jedną akcją — przy błędzie naprawiasz to dniami.

### Repricer

Baselinker ma wbudowany repricer — automatycznie obniża lub podwyższa cenę, jeśli konkurent ją zmieni. Konfigurujesz **reguły**: minimalna cena, maksymalna cena, krok, częstotliwość. **Włącz dopiero po stabilizacji** ofert — wcześniej tylko zaciemnia analizę sprzedaży.

## {automatyzacje} Automatyzacje statusów i komunikacji

To miejsce, w którym Baselinker zarabia na siebie najszybciej. Najważniejsze scenariusze:

- **Status "Opłacone" → automatyczne przyjęcie do realizacji** + wygenerowanie listu przewozowego dla domyślnego kuriera.
- **Status "Wysłane" → automatyczna zmiana statusu na Allegro** + wysyłka numeru paczki przez API Allegro (działa świetnie z [integracją InPost ShipX](/blog/inpost-paczkomaty-integracja-baselinker)).
- **Status "Zwrot" → automatyczne uznanie zwrotu w Allegro** + wystawienie korekty faktury (zobacz [automatyzację faktur w Fakturowni](/blog/automatyzacja-faktur-fakturownia-baselinker)).
- **Wiadomość od kupującego → automatyczna odpowiedź szablonowa** w zależności od słowa kluczowego.

> **Bezwzględnie włącz "Wysyłka numeru paczki do Allegro"**. Każda paczka bez trackingu = ryzyko negatywnej opinii i blokady ofert w wynikach.

## {bledy} Najczęstsze błędy i jak ich unikać

**1. Wystawianie bez szablonu polityki zwrotów.** Allegro odrzuci ofertę. Skonfiguruj polityki **przed** pierwszym wystawieniem.

**2. Brak EAN w produktach.** Część kategorii (elektronika, kosmetyki) wymaga EAN. Bez niego ofert nie wystawisz masowo.

**3. Synchronizacja stanu w jedną stronę.** Sklep sprzedaje produkt, Allegro nadal go pokazuje, klient zamawia coś, czego nie masz. Zawsze synchronizuj w obie strony.

**4. Repricer włączony od dnia 1.** Algorytm Allegro karze za zbyt częste zmiany cen. Włącz repricer po 2-3 tygodniach stabilnej sprzedaży.

**5. Brak monitoringu błędów.** W Baselinkerze jest zakładka **Allegro → Błędy**. Sprawdzaj ją codziennie pierwsze 2 tygodnie.

## {podsumowanie} Podsumowanie

Integracja Baselinker + Allegro to nie jest projekt na popołudnie. To 1-2 tygodnie pracy dla doświadczonego operatora, ale efekt jest zauważalny od pierwszego miesiąca:

- 70-80% mniej czasu na obsługę ofert,
- 0 błędów typu "sprzedałem, czego nie miałem",
- automatyczna komunikacja z kupującymi,
- przewidywalna marża dzięki repricerowi.

Jeśli chcesz, żebym poprowadził to wdrożenie u Ciebie — odezwij się. Pierwsza konsultacja jest bezpłatna i kończy się konkretną wyceną — możesz też samodzielnie [policzyć ROI w kalkulatorze](/kalkulator) lub sprawdzić [cennik wdrożeń](/cennik).`,
  },

  // ============================================
  // 10. Automatyzacja faktur Fakturownia + Baselinker
  // ============================================
  {
    slug: "automatyzacja-faktur-fakturownia-baselinker",
    title: "Automatyzacja faktur w Fakturowni + Baselinker — przewodnik 2026",
    metaDescription:
      "Jak skonfigurować automatyczne wystawianie faktur i paragonów w Fakturowni z poziomu Baselinkera. Reguły, szablony, wysyłka mailem, integracja z księgowością.",
    ogTitle: "Fakturownia × Baselinker — automatyzacja faktur 2026",
    ogDescription: "Faktury, paragony i JPK_FA bez ręcznej pracy. Pokazuję, jak spiąć Fakturownię z Baselinkerem od A do Z.",
    ogImage: "https://synctech.pl/og-fakturownia-baselinker.jpg",
    excerpt:
      "Ręczne wystawianie faktur to 2-3 godziny pracy dziennie. Pokazuję, jak skonfigurować Fakturownię + Baselinkera, żeby faktury wystawiały się same i trafiały do klienta i księgowej.",
    category: "Integracje",
    publishedAt: PL_DATE_2026_04_30,
    readingTime: 11,
    keywords: ["fakturownia baselinker", "automatyczne faktury baselinker", "integracja fakturownia"],
    toc: [
      { id: "po-co", label: "Po co automatyzować faktury" },
      { id: "wymagania", label: "Wymagania wstępne" },
      { id: "konfiguracja", label: "Konfiguracja integracji" },
      { id: "reguly", label: "Reguły wystawiania (FV vs paragon)" },
      { id: "wysylka", label: "Automatyczna wysyłka do klienta" },
      { id: "ksiegowosc", label: "Eksport do księgowości" },
      { id: "bledy", label: "Najczęstsze błędy i jak ich unikać" },
      { id: "podsumowanie", label: "Podsumowanie" },
    ],
    related: ["automatyzacja-zamowien-baselinker", "integracja-baselinker-allegro-przewodnik", "baselinker-koszty-wdrozenia-2026"],
    faqs: [
      {
        q: "Czy Fakturownia obsługuje JPK_FA i JPK_VAT?",
        a: "Tak. Fakturownia generuje plik JPK_FA z faktur z dowolnego okresu jednym kliknięciem i pozwala wysłać go bezpośrednio do US lub przekazać księgowej. JPK_VAT też jest dostępny, ale zwykle robi go biuro rachunkowe na podstawie pełnego obrotu firmy.",
      },
      {
        q: "Co jeśli klient nie poda NIP, a potem poprosi o fakturę?",
        a: "Możesz wystawić fakturę imienną do paragonu w terminie 3 miesięcy. W Baselinkerze konfigurujesz osobną akcję 'Wystaw fakturę do paragonu' uruchamianą ręcznie, gdy klient prześle prośbę z numerem zamówienia.",
      },
      {
        q: "Czy faktury z Baselinkera są zgodne z KSeF?",
        a: "Fakturownia obsługuje wysyłkę do KSeF od 2024 r. Po wystawieniu faktury z Baselinkera, Fakturownia automatycznie przesyła ją do KSeF i pobiera numer referencyjny. Wymaga to wcześniejszej konfiguracji tokenu KSeF w panelu Fakturowni.",
      },
      {
        q: "Co z fakturami za zwroty i reklamacje?",
        a: "Konfigurujesz w Baselinkerze automatyzację: status 'Zwrot zaakceptowany' → akcja 'Wystaw korektę w Fakturowni'. Korekta odwołuje się do faktury źródłowej, koryguje kwoty i automatycznie trafia mailem do klienta + do księgowej.",
      },
      {
        q: "Czy mogę używać Fakturowni razem z systemem ERP (Subiekt, Comarch)?",
        a: "Tak, ale wybierz jedno źródło prawdy. Najczęstszy układ: Fakturownia wystawia faktury z Baselinkera, raz dziennie eksportuje JPK_FA lub plik wymiany, który ERP importuje. Wystawianie tych samych faktur w dwóch systemach prowadzi do dublowania numeracji.",
      },
    ],
    content: `## {po-co} Po co automatyzować faktury

Mały sklep z 30 zamówieniami dziennie traci na wystawianiu faktur **ok. 2 godziny dziennie**. Sklep z 200 zamówieniami — całe etaty. Automatyzacja [Fakturowni](/integracje/fakturownia) + [Baselinkera](/platformy/baselinker) eliminuje ten koszt prawie w 100%.

Konkretne efekty po wdrożeniu:

- faktury wystawiają się w **2-3 minuty po opłaceniu** zamówienia,
- klient dostaje fakturę **mailem od razu**, bez przypominania,
- księgowa ma **automatyczny eksport** raz dziennie/tygodniowo,
- 0 pomyłek w danych (NIP, kwoty, stawki VAT).

## {wymagania} Wymagania wstępne

- **Konto Fakturownia** (minimum plan z dostępem do API — Pro lub wyżej).
- **Konto Baselinker** z aktywną integracją sklepu i kanałów sprzedaży.
- **Zdefiniowane stawki VAT** w katalogu Baselinkera (każdy produkt musi mieć stawkę).
- **Numeracja faktur** ustalona w Fakturowni (osobna seria dla sklepu, jeśli to potrzebne).
- **Szablon wiadomości email** wysyłającej fakturę do klienta.

## {konfiguracja} Konfiguracja integracji

W Baselinkerze: **Integracje → Faktury → Fakturownia → Dodaj konto**. Potrzebny będzie:

- adres Twojej Fakturowni (np. \`mojafirma.fakturownia.pl\`),
- klucz API (znajdziesz w Fakturowni: **Ustawienia → Integracje → API**).

### Co skonfigurować od razu

1. **Domyślna seria numeracji** — np. \`FV/{NR}/{MM}/{YYYY}\`.
2. **Domyślna metoda płatności** — przelew, karta, BLIK, pobranie.
3. **Termin płatności** — dla B2B zwykle 14 dni, dla B2C "zapłacono".
4. **Domyślna stawka VAT** — fallback, jeśli produkt nie ma swojej.
5. **Język faktury** — PL/EN/DE w zależności od rynku.

> **Tip:** stwórz **2 osobne serie numeracji** — jedną dla B2C (paragony/uproszczone), drugą dla B2B (pełne faktury). Księgowa Ci podziękuje.

## {reguly} Reguły wystawiania (FV vs paragon)

Baselinker pozwala wystawiać dokumenty **warunkowo**. Najczęstsze scenariusze:

### Scenariusz 1: Klient B2C bez NIP

- → wystaw **paragon** automatycznie po opłaceniu,
- → wyślij PDF mailem,
- → status zamówienia: "Do wysyłki".

### Scenariusz 2: Klient B2C z prośbą o fakturę (NIP w komentarzu)

- → wystaw **fakturę imienną** lub na firmę,
- → wyślij PDF mailem,
- → status zamówienia: "Do wysyłki".

### Scenariusz 3: Klient B2B (NIP w danych zamówienia)

- → wystaw **fakturę VAT** z terminem 14 dni,
- → wyślij PDF mailem + dodaj do paczki,
- → status zamówienia: "Do wysyłki".

### Scenariusz 4: Zamówienie z marketplace (Allegro/Amazon)

- → wystaw **fakturę** (Allegro wymaga FV dla każdego zamówienia od 2024 r. — więcej w [przewodniku po integracji Baselinker + Allegro](/blog/integracja-baselinker-allegro-przewodnik)),
- → wyślij PDF do kupującego przez API Allegro,
- → dodaj kopię do paczki (np. razem z etykietą [InPost Paczkomaty](/blog/inpost-paczkomaty-integracja-baselinker)).

W Baselinkerze konfigurujesz to w **Automatyzacje → Akcje → Wystaw dokument** z warunkami (np. \`jeśli pole NIP nie jest puste → wystaw fakturę VAT\`).

## {wysylka} Automatyczna wysyłka do klienta

Faktura, która leży w Fakturowni i nie trafia do klienta, to faktura bezużyteczna. Skonfiguruj **automatyczną wysyłkę mailem**:

- w Fakturowni: **Ustawienia → Email** → włącz wysyłkę po wystawieniu,
- szablon emaila: krótki, z linkiem do faktury PDF + numerem zamówienia,
- **załączaj PDF**, nie tylko link (klienci nie klikają w linki),
- nadawca: \`faktury@twojadomena.pl\` (nie no-reply, klient odpisuje na pytania).

> **Bezpiecznik:** monitoruj odbicia (bounce) z domeny faktury. Jeśli 5% maili wraca, masz problem z konfiguracją SPF/DKIM. Klienci nie dostają faktur i myślą, że oszukujesz.

## {ksiegowosc} Eksport do księgowości

Faktury mają sens, jeśli trafiają do księgowej w formacie, który umie zaimportować. Fakturownia obsługuje:

- **eksport XML** (JPK_FA, JPK_VAT) — standard dla księgowych,
- **eksport CSV** — dla księgowych pracujących w Excelu,
- **bezpośrednią integrację** z popularnymi systemami (Comarch, Symfonia, WAPRO, Subiekt — zobacz [integracje ERP z Baselinkerem](/integracje/erp-baselinker)),
- **dostęp dla księgowej** — osobne konto z prawami "tylko odczyt".

### Najprostszy układ

1. Faktury wystawiają się automatycznie z Baselinkera.
2. Raz w tygodniu Fakturownia wysyła mailem do księgowej **paczkę PDF + plik JPK_FA**.
3. Księgowa importuje JPK_FA do swojego systemu jednym kliknięciem.
4. Koniec.

## {bledy} Najczęstsze błędy i jak ich unikać

**1. Brak stawki VAT przy produkcie.** Baselinker wystawi fakturę z fallbackiem (zwykle 23%), a Ty masz produkt z 5% VAT. Zawsze ustawiaj stawkę przy każdym produkcie.

**2. Numeracja faktur się rozjeżdża.** Jeśli wystawiasz część ręcznie w Fakturowni, część automatycznie z Baselinkera — będą luki. Zdecyduj: wszystko automatycznie albo nic.

**3. Brak weryfikacji NIP.** Klient wpisze "1234567890" jako NIP, faktura się wystawi, urząd to potem znajdzie. **Włącz walidację NIP** w Baselinkerze.

**4. Faktury w zwrotach.** Klient zwraca produkt — musisz wystawić **korektę**, nie nową fakturę. Skonfiguruj automatyczną korektę przy statusie "Zwrot zaakceptowany".

**5. Brak archiwum.** Fakturownia trzyma dane, ale **rób backup** raz w miesiącu. JPK_FA + PDF na zewnętrzny dysk.

## {podsumowanie} Podsumowanie

Konfiguracja Fakturowni + Baselinkera to 1-2 dni pracy. Zwrot inwestycji: **pierwszy miesiąc**, bo oszczędzasz minimum etat na ręcznym wystawianiu i wysyłaniu faktur.

Kluczowe zasady:

- automatyzuj **od pierwszego dnia**, nie odkładaj na "jak będzie więcej zamówień",
- rozdziel B2B i B2C w numeracji,
- wysyłaj faktury **z domeny, nie z no-reply**,
- daj księgowej dostęp tylko do odczytu — nie wymyślaj plików email.

Chcesz, żebym wdrożył to u Ciebie? **Standardowy projekt: 2-3 dni, 2-4 tys. zł, ROI w pierwszym miesiącu** ([policz w kalkulatorze](/kalkulator)). Odezwij się.`,
  },

  // ============================================
  // 11. InPost Paczkomaty + Baselinker
  // ============================================
  {
    slug: "inpost-paczkomaty-integracja-baselinker",
    title: "InPost Paczkomaty — integracja z Baselinker krok po kroku 2026",
    metaDescription:
      "Jak zintegrować InPost Paczkomaty z Baselinkerem: konfiguracja API, geowidget na sklepie, masowe etykiety, automatyczne statusy i obsługa zwrotów.",
    ogTitle: "InPost × Baselinker — integracja Paczkomatów 2026",
    ogDescription: "Geowidget, masowe etykiety i automatyczne statusy. Pełna integracja Paczkomatów InPost z Baselinkerem.",
    ogImage: "https://synctech.pl/og-inpost-baselinker.jpg",
    excerpt:
      "Paczkomaty to dziś 60-70% wyboru dostawy w polskim e-commerce. Pokazuję, jak połączyć InPost z Baselinkerem tak, żeby etykiety i statusy generowały się same.",
    category: "Logistyka",
    publishedAt: PL_DATE_2026_05_05,
    readingTime: 11,
    keywords: ["inpost baselinker", "paczkomaty baselinker", "integracja inpost"],
    toc: [
      { id: "po-co", label: "Po co automatyzować InPost w Baselinkerze" },
      { id: "wymagania", label: "Wymagania wstępne" },
      { id: "konfiguracja", label: "Konfiguracja konta i API" },
      { id: "geowidget", label: "Geowidget na sklepie" },
      { id: "etykiety", label: "Masowe etykiety i nadania" },
      { id: "statusy", label: "Automatyczne statusy i komunikacja" },
      { id: "zwroty", label: "Obsługa zwrotów (Szybkie Zwroty)" },
      { id: "bledy", label: "Najczęstsze błędy i jak ich unikać" },
      { id: "podsumowanie", label: "Podsumowanie" },
    ],
    related: ["jak-wybrac-kuriera-dla-sklepu-internetowego", "automatyzacja-zamowien-baselinker", "jak-polaczyc-baselinker-z-shopify"],
    faqs: [
      {
        q: "Czy do integracji InPost z Baselinkerem potrzebuję umowy bezpośredniej z InPost?",
        a: "Tak. Baselinker łączy się z Twoim kontem InPost ShipX, ale to Ty masz umowę i cennik z InPost. Alternatywą są pośrednicy (Apaczka, Furgonetka), którzy też mają integrację z Baselinkerem, ale stawki są zwykle wyższe niż bezpośrednio.",
      },
      {
        q: "Czym różni się ShipX od starego API InPost?",
        a: "ShipX to nowe API z webhookami, lepszą obsługą statusów i Szybkimi Zwrotami. Stary moduł 'Allegro/InPost' działa, ale nie dostaje już nowych funkcji. Wszystkie nowe wdrożenia rób na ShipX — InPost stopniowo wygasza stare API.",
      },
      {
        q: "Co zrobić, gdy klient wybrał paczkomat, którego już nie ma?",
        a: "InPost wycofuje paczkomaty z eksploatacji. Jeśli klient wybrał taki przez stary geowidget, etykieta się nie wygeneruje. Skonfiguruj w Baselinkerze regułę 'błąd nadania → wyślij do klienta mail z linkiem do nowego wyboru paczkomatu' i automatycznie pobierz nowy kod.",
      },
      {
        q: "Czy mogę nadawać paczki InPost z paczkomatu nadawczego zamiast zamawiać kuriera?",
        a: "Tak. W konfiguracji Baselinkera wybierz 'Nadanie w paczkomacie POP', podaj swój domyślny POP, drukuj manifest zbiorczy i wrzuć paczki za jednym razem. Oszczędza to koszt odbioru kurierskiego, ale ma sens dopiero przy paczkach poniżej rozmiaru C.",
      },
      {
        q: "Czy Szybkie Zwroty InPost są płatne dla sklepu?",
        a: "Tak, sklep płaci za każdy zwrot zrealizowany przez Szybkie Zwroty (cennik wg umowy, zwykle 8-12 zł netto). Klient nadaje paczkę bezpłatnie. To i tak najtańsza forma zwrotu paczkomatowego — alternatywy (kurier z odbiorem) kosztują 15-25 zł.",
      },
    ],
    content: `## {po-co} Po co automatyzować InPost w Baselinkerze

InPost Paczkomaty to dziś najczęściej wybierana metoda dostawy w polskim e-commerce — wg danych operatora **ponad 60% przesyłek B2C w 2025 r.** Każda paczka nadana ręcznie to 3-5 minut pracy. Przy 100 paczkach dziennie tracisz pół etatu.

Po integracji z [Baselinkerem](/platformy/baselinker):

- **etykiety generują się masowo** w 30 sekund (100 paczek),
- **numer paczki trafia automatycznie do klienta** + na Allegro (zobacz [pełen przewodnik Baselinker + Allegro](/blog/integracja-baselinker-allegro-przewodnik)),
- **statusy aktualizują się same** (nadane → odebrane → wydane) — to część szerszej [automatyzacji zamówień](/blog/automatyzacja-zamowien-baselinker),
- **zwroty** obsługiwane są przez InPost Szybkie Zwroty bez pracy zespołu (z automatyczną korektą faktury w [Fakturowni](/blog/automatyzacja-faktur-fakturownia-baselinker)).

## {wymagania} Wymagania wstępne

- **Umowa z InPost** (Manager Paczki / ShipX) z aktywnym kontem rozliczeniowym.
- **Login + hasło do panelu Manager Paczki** + dostęp do API ShipX.
- **Konto Baselinker** z aktywną integracją źródeł zamówień (porównanie kurierów: [jak wybrać kuriera dla sklepu](/blog/jak-wybrac-kuriera-dla-sklepu-internetowego), pełen przegląd dostępny w [integracjach logistycznych](/integracje/logistyka-kurierzy)).
- **Drukarka etykiet** (termiczna 100×150 mm) lub zwykła A4 + nożyczki/składarka.
- **Adres nadawcy** zarejestrowany w InPost (z numerem POP, jeśli wysyłasz z paczkomatu).

## {konfiguracja} Konfiguracja konta i API

W Baselinkerze: **Integracje → Kurierzy → InPost → Dodaj konto**. Wybierz **InPost ShipX** (nowe API), nie stary moduł "Allegro/InPost".

### Co podać przy konfiguracji

1. **Organization ID** (znajdziesz w panelu Manager Paczki → Ustawienia → API).
2. **Token API** (wygeneruj w tym samym miejscu, zapisz w bezpiecznym miejscu).
3. **Domyślny adres nadania** — z dokładnością do POP, jeśli używasz paczkomatu jako nadawca.
4. **Domyślne usługi** — Paczkomat, Kurier, Allegro Smart!, COD (za pobraniem).
5. **Wymiary i waga** — fallback, jeśli zamówienie ich nie ma (zwykle 30×20×10 cm / 1 kg).

> **Bezwzględnie** włącz **tryb produkcyjny**, nie sandbox. Sandbox generuje etykiety, ale InPost ich nie przyjmie.

## {geowidget} Geowidget na sklepie

Klient nie wybierze paczkomatu, jeśli musi wpisać 6-cyfrowy kod. Wstaw na sklepie **geowidget InPost** — mapę z paczkomatami w pobliżu adresu klienta.

### Jak to zrobić

1. W panelu InPost: **Geowidget → Wygeneruj klucz API geowidget**.
2. W sklepie (Shopify/WooCommerce/Shoper) zainstaluj wtyczkę "InPost Paczkomaty" i wklej klucz.
3. Skonfiguruj **mapowanie metody dostawy** — w sklepie metoda "Paczkomat", w Baselinkerze ma się mapować na "InPost Paczkomaty 24/7".
4. Sprawdź, czy do zamówienia przychodzi **kod paczkomatu** (np. \`KRA01A\`).

> **Jeśli kod paczkomatu nie przychodzi** do Baselinkera — Twój sklep nie przekazuje pola \`pickup_point\`. Bez tego pola etykiety wygenerują się błędnie. Sprawdź to **przed** pierwszym nadaniem.

## {etykiety} Masowe etykiety i nadania

To główna funkcja, dla której robisz integrację. Codzienny workflow:

1. Rano otwórz Baselinker → **Zamówienia → Filtr: status "Do wysyłki"**.
2. Zaznacz wszystkie (np. 80 paczek).
3. **Akcje masowe → Generuj etykiety InPost**.
4. Baselinker tworzy paczki w ShipX, pobiera PDF z etykietami, scala w jeden dokument.
5. Drukujesz, naklejasz, pakujesz.
6. **Akcje masowe → Zamów odbiór kuriera** (jeśli kurier przyjeżdża do Ciebie) lub **Wygeneruj manifest** (jeśli wysyłasz przez paczkomat).

### Manifest dla paczkomatu

Jeśli wrzucasz paczki do paczkomatu nadawczego, **drukuj manifest** (lista paczek z kodami kreskowymi). InPost skanuje go jednym ruchem i nie musisz potwierdzać każdej paczki osobno.

## {statusy} Automatyczne statusy i komunikacja

W Baselinkerze ustaw **automatyzacje**:

- **Etykieta wygenerowana → status "Nadane"** + email do klienta z numerem paczki + linkiem do śledzenia.
- **Paczka odebrana przez kuriera (webhook InPost) → status "W drodze"** + SMS (opcjonalnie).
- **Paczka dostarczona do paczkomatu → status "Do odbioru"** + email "Twoja paczka czeka".
- **Paczka odebrana przez klienta → status "Zakończone"** + prośba o opinię (po 3 dniach).

> **InPost wysyła własne powiadomienia SMS** (kod do paczkomatu, powiadomienie o odbiorze). Nie duplikuj ich — klient dostanie 4 SMS-y o tej samej paczce i się wkurzy.

## {zwroty} Obsługa zwrotów (Szybkie Zwroty)

InPost ma świetne **Szybkie Zwroty** — klient nadaje paczkę bezpłatnie z paczkomatu, Ty płacisz dopiero przy odbiorze. W Baselinkerze:

1. **Integracje → InPost → Szybkie Zwroty → Włącz**.
2. Wygeneruj **link do formularza zwrotu** dla swojego sklepu (np. \`zwroty.twojadomena.pl\`).
3. Wyślij link w mailu po dostawie albo umieść na stronie "Zwroty".
4. Klient wypełnia formularz, dostaje kod do paczkomatu, nadaje paczkę.
5. Zwrot trafia do Ciebie + automatycznie pojawia się w Baselinkerze jako "Zwrot oczekujący".
6. Po odbiorze paczki — Baselinker tworzy korektę faktury i zwraca pieniądze (jeśli skonfigurujesz to z bramką płatności).

## {bledy} Najczęstsze błędy i jak ich unikać

**1. Brak pola \`pickup_point\` w zamówieniu.** Etykieta wygeneruje się na kurierską, klient pójdzie do paczkomatu i nie znajdzie paczki. Sprawdź pole **przed** masowym nadaniem.

**2. Złe wymiary paczek.** InPost ma 3 rozmiary skrytek (A/B/C). Jeśli wpiszesz "C" dla paczki, która mieści się w "A" — płacisz drożej. Skonfiguruj **regułę wagowo-wymiarową** w Baselinkerze.

**3. Etykiety bez kodu kreskowego.** Drukarka termiczna z niskim DPI rozmazuje kreski. **Zawsze testuj** etykiety na drukarce produkcyjnej zanim wystartujesz.

**4. Brak monitoringu webhooków.** Jeśli webhook InPost nie dochodzi (np. firewall), statusy się nie aktualizują. Raz w tygodniu sprawdzaj **Logi → Webhooki InPost**.

**5. Pomylenie kont sandbox/prod.** Wygenerujesz 200 etykiet na sandboxie i okaże się, że InPost ich nie przyjmie. Zawsze sprawdzaj, że jesteś w trybie produkcyjnym.

## {podsumowanie} Podsumowanie

Integracja InPost + Baselinker to nie jest opcja "fajnie mieć" — to standard. Bez niej tracisz minimum **2-3 godziny pracy dziennie** i wystawiasz się na błędy ludzkie (zła etykieta = strata paczki).

Po wdrożeniu:

- **80-90% mniej czasu** na obsługę wysyłek,
- **0 błędów** w adresach paczkomatów,
- **Lepsza opinia klientów** dzięki szybkim statusom i Szybkim Zwrotom,
- **Skalowalność** — z 50 paczek dziennie spokojnie idziesz do 500 bez nowych etatów.

Chcesz, żebym wdrożył to u Ciebie? **Standardowy projekt: 2-3 dni, 2-4 tys. zł** ([policz oszczędność w kalkulatorze](/kalkulator)). Pierwsza konsultacja jest bezpłatna — odezwij się.`,
  },
];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug: string): BlogPost[] {
  const post = getPostBySlug(slug);
  if (!post) return [];
  return post.related
    .map(getPostBySlug)
    .filter((p): p is BlogPost => Boolean(p));
}
